(function () {
  if (customElements.get('product-customization')) return;

  // Un solo salvataggio alla volta, anche se product-info viene clonato
  // durante un cambio colore/bundle (due <product-customization> per ~500ms).
  let melideCustomizationSaveLock = false;

  // Consentiti: lettere, numeri, spazi, . , ' - & / (slash ricamabile).
  // L'a capo (Invio) è consentito; nel carrello viene salvato come " \ ".
  const DISALLOWED_TEXT_PATTERN = /[^\p{L}\p{N}\s.,'\-&/]/gu;
  const HAS_DISALLOWED_PATTERN = /[^\p{L}\p{N}\s.,'\-&/]/u;
  // Modalità "solo numeri": accetta cifre, spazi e a capo.
  const DISALLOWED_NUMBERS_PATTERN = /[^\d\s]/gu;
  const HAS_DISALLOWED_NUMBERS_PATTERN = /[^\d\s]/u;
  const MELIDE_SNAPSHOT_PROPERTY = '_melide_snapshot';
  const MELIDE_LETTERS_KEY = '_Lettere';
  // Label corte IT: checkout (locale IT), backoffice (_ se locale ≠ IT), snapshot.
  const MELIDE_ADMIN_LABELS = {
    cost: 'Costo Personalizzazione',
    text: 'Testo',
    color: 'Colore ricamo',
    position: 'Posizione',
    font: 'Font',
    notes: 'Note',
  };
  // Ordine chiavi nello snapshot (= embroidery-display-config, senza __costo__).
  const MELIDE_SNAPSHOT_KEYS = [
    MELIDE_ADMIN_LABELS.cost,
    MELIDE_ADMIN_LABELS.text,
    MELIDE_ADMIN_LABELS.color,
    MELIDE_ADMIN_LABELS.position,
    MELIDE_ADMIN_LABELS.font,
    MELIDE_ADMIN_LABELS.notes,
  ];
  // Marcatore a capo nel carrello/ordine (back office). Usiamo " \ " così "/"
  // resta disponibile nel testo ricamabile.
  const NEWLINE_MARKER = ' \\ ';

  function countableCharLength(value) {
    return (value || '').replace(/\s+/g, '').length;
  }

  function limitByCountableChars(value, max) {
    if (!value) return '';
    const limit = Number.isFinite(max) ? max : Infinity;
    let count = 0;
    let out = '';
    for (const ch of value) {
      if (/\s/.test(ch)) {
        out += ch;
        continue;
      }
      if (count >= limit) continue;
      count += 1;
      out += ch;
    }
    return out;
  }

  function slugifyColorName(name) {
    const n = (name || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
    const stoneWashed = /\bstone|\bstonewashed|\bwashed\b/.test(n);
    if (stoneWashed && /\bros[ao]\b|\bpink\b/.test(n)) return 'rosa';
    if (stoneWashed && /\bner[oa]?\b|\bblack\b/.test(n)) return 'nero-stoned';
    if (/\bperla\b/.test(n)) return 'bianco-perla';
    if (/\bantracite\b|\bcharcoal\b|\bcarbone\b/.test(n)) return 'grigio';
    if (/\bros[ao]\b|\bpink\b/.test(n)) return 'rosa';
    if (/\bner[oa]?\b|\bblack\b/.test(n)) return 'nero';
    if (/\bbianc[oa]?\b|\bwhite\b|\bpanna\b|\bcream\b|\bivory\b|\bghiaccio\b/.test(n)) return 'bianco';
    if (/\bblu\b|\bnavy\b|\bblue\b/.test(n)) return 'blu';
    if (/\bgrigi[oa]?\b|\bgrey\b|\bgray\b|\bmelange\b/.test(n)) return 'grigio';
    if (/\bnaturale\b|\bbeige\b|\becru\b|\bcrudo\b/.test(n)) return 'naturale';
    return n.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function slugifyThreadName(name) {
    return (name || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function forbiddenThreadSlugs(garmentSlug, garmentType) {
    const slug = garmentSlug || '';
    if ((slug === 'bianco' || slug === 'bianco-perla' || slug === 'naturale') && garmentType !== 'borsone') return ['bianco'];
    if (slug === 'grigio') return garmentType === 'tshirt' ? ['nero'] : ['bianco'];
    if (slug === 'nero' || slug === 'nero-stoned') return ['nero'];
    if (slug === 'blu' || slug === 'blu-notte') return ['nero', 'blu-notte'];
    if (slug === 'rosa') return ['rosa'];
    return [];
  }

  function firstProp(properties, keys) {
    if (!properties) return '';
    for (const key of keys) {
      if (properties[key]) return properties[key];
    }
    return '';
  }

  function getEmbroideryProp(properties, field, localeLabels) {
    const adminKey = MELIDE_ADMIN_LABELS[field];
    const localeKey = localeLabels?.[field] || adminKey;
    return firstProp(properties, [localeKey, adminKey, `_${adminKey}`]);
  }

  function hasEmbroideryText(properties) {
    return Boolean(properties?.[MELIDE_LETTERS_KEY]);
  }

  // I bundle nativi spostano le properties sul lineItemGroup (Order Printer non le
  // legge). Copia tecnica _melide_snapshot sulla riga fee / prodotto
  // (_ prefix = nascosta al cliente; collegata via _personalization_id).
  function serializeMelideSnapshot(canonical, { productTitle, linkId }) {
    const parts = [];
    if (productTitle) parts.push(`_bundle::${productTitle}`);
    if (linkId) parts.push(`_personalization_id::${linkId}`);
    for (const key of MELIDE_SNAPSHOT_KEYS) {
      if (canonical[key]) parts.push(`${key}::${canonical[key]}`);
    }
    return parts.join('|');
  }

  // Rimuove caratteri non consentiti (incluse emoji), collassa gli spazi
  // orizzontali multipli in uno solo e le righe vuote multiple in un solo a
  // capo, senza spazio/a capo iniziale. Conserva i ritorni a capo voluti.
  function sanitizeWhileTyping(value, onlyNumbers) {
    const pattern = onlyNumbers ? DISALLOWED_NUMBERS_PATTERN : DISALLOWED_TEXT_PATTERN;
    return (value || '')
      .replace(pattern, '')
      .replace(/\r/g, '')
      .replace(/[^\S\n]+/g, ' ')
      .replace(/\n{2,}/g, '\n')
      .replace(/^[^\S]+/, '');
  }

  // Converte gli a capo reali nel marcatore leggibile per il carrello.
  function encodeLineBreaks(text) {
    return (text || '').replace(/\n+/g, NEWLINE_MARKER);
  }

  // Converte il marcatore del carrello di nuovo in a capo reali (edit mode).
  function decodeLineBreaks(text) {
    if (!text) return '';
    return text.split(NEWLINE_MARKER).join('\n');
  }

  class ProductCustomization extends HTMLElement {
    // I controlli del ricamo vivono nel drawer, che dopo il "portal" viene
    // spostato in <body>. Queste helper cercano sempre nel punto giusto.
    control(selector) {
      return (this.drawer || this).querySelector(selector);
    }

    controlAll(selector) {
      return (this.drawer || this).querySelectorAll(selector);
    }

    connectedCallback() {
      this.productForm = this.closest('product-form') || document.querySelector('product-form');
      this.overlay = this.querySelector('[data-customization-overlay]');
      this.drawer = this.querySelector('[data-customization-drawer]');
      this.openBtn = this.querySelector('[data-customization-open]');
      this.editBtn = this.querySelector('[data-customization-edit]');
      this.shareBtn = this.querySelector('[data-customization-share]');
      this.shareFeedback = this.querySelector('[data-customization-share-feedback]');
      this.closeBtn = this.querySelector('[data-customization-close]');
      this.saveBtn = this.querySelector('[data-customization-save]');
      this.messageInput = this.querySelector('[data-customization-message]');
      this.noteInput = this.querySelector('[data-customization-note]');
      this.preview = this.querySelector('[data-customization-preview]');
      this.previewText = this.querySelector('[data-customization-preview-text]');
      this.previewImages = this.querySelectorAll('[data-customization-preview-image]');
      this.priceEl = this.querySelector('[data-customization-price]');
      this.summaryEl = this.querySelector('[data-customization-summary]');
      this.summaryText = this.querySelector('[data-customization-summary-text]');
      this.summaryStyle = this.querySelector('[data-customization-summary-style]');
      this.summaryPosition = this.querySelector('[data-customization-summary-position]');
      this.summaryColor = this.querySelector('[data-customization-summary-color]');
      this.summaryNote = this.querySelector('[data-customization-summary-note]');
      this.colorLabel = this.querySelector('[data-customization-color-label]');
      this.productColorLabel = this.querySelector('[data-customization-product-color-label]');
      this.summaryPrice = this.querySelector('[data-customization-summary-price]');
      this.summaryTotal = this.querySelector('[data-customization-summary-total]');
      this.summaryTotalValue = this.querySelector('[data-customization-summary-total-value]');
      this.savedNotice = this.querySelector('[data-customization-saved-notice]');
      this.charCountEl = this.querySelector('[data-customization-char-count]');
      this.inlineChars = this.querySelector('[data-customization-inline-chars]');
      this.inlineFee = this.querySelector('[data-customization-inline-fee]');
      this.validationError = this.querySelector('[data-customization-validation-error]');
      this.hiddenCharCount = this.querySelector('[data-customization-hidden-char-count]');
      this.hiddenPrice = this.querySelector('[data-customization-hidden-price]');
      this.hiddenLinkId = this.querySelector('[data-customization-hidden-link-id]');
      this.summarySpinnerEl = this.querySelector('[data-customization-summary-spinner]');
      this.summarySpinnerMessageEl = this.querySelector('[data-customization-summary-spinner-message]');
      this.drawerSpinnerEl = this.querySelector('[data-customization-drawer-spinner]');
      this.drawerSpinnerMessageEl = this.querySelector('[data-customization-drawer-spinner-message]');
      this.saveLabel = this.querySelector('[data-customization-save-label]');
      this.saveSpinner = this.querySelector('[data-customization-save-spinner]');
      this.saveError = this.querySelector('[data-customization-save-error]');

      this.charPrice = parseFloat(this.dataset.charPrice || '1.5');
      this.minChars = parseInt(this.dataset.minChars || '1', 10);
      this.maxChars = parseInt(this.dataset.maxChars || '50', 10);
      this.onlyNumbers = this.dataset.onlyNumbers === 'true';
      this.required = this.dataset.required !== 'false';
      this.feeVariantId = this.dataset.feeVariantId || '';
      this.feeIncluded = this.dataset.feeIncluded === 'true' || !this.feeVariantId;
      this.basePriceCents = parseInt(this.dataset.basePriceCents || '0', 10);
      this.productId = this.dataset.productId || '';
      // Label checkout localizzate (da locales → data-property-labels).
      this.propertyLabels = { ...MELIDE_ADMIN_LABELS };
      try {
        const parsedLabels = JSON.parse(this.dataset.propertyLabels || '{}');
        this.propertyLabels = { ...MELIDE_ADMIN_LABELS, ...parsedLabels };
      } catch (error) {
        this.propertyLabels = { ...MELIDE_ADMIN_LABELS };
      }
      this.saved = false;
      this.savedSnapshot = null;
      this._syncingProductColor = false;
      this._pendingDrawerColor = null;
      this._pendingOptionQueue = [];
      this._pendingVariantId = null;
      this._confirmedProductColor = '';
      this.previewMap = {};
      try {
        this.previewMap = JSON.parse(this.dataset.previewMap || '{}');
      } catch (error) {
        this.previewMap = {};
      }
      this.garmentType = this.dataset.garmentType || 'tshirt';
      // Riga carrello che stiamo modificando (solo in edit mode da carrello).
      this.cartPersonalizationId = null;
      this.editMode = false;
      this.cartEditLineKey = null;
      this.cartEditFeeLineKey = null;
      this.cartEditVariantId = null;
      this.cartEditQuantity = 1;
      this._restoringFromCart = false;
      this.selectedVariant = null;
      this.isProcessing = false;
      this.defaultSavedNoticeText =
        this.savedNotice?.textContent?.trim() ||
        'Personalizzazione salvata. Ora puoi scegliere taglia, colore e aggiungere al carrello.';

      if (this.productForm) {
        // Il gate (obbligo di personalizzare prima di aggiungere) vale solo
        // per i prodotti con form_required = true.
        if (this.required) {
          this.productForm.setAttribute('data-require-customization', 'true');
        }
        this.form =
          this.productForm.querySelector('form') ||
          document.querySelector('form[data-type="add-to-cart-form"]');
        if (this.form) {
          this.form.addEventListener('submit', this.onFormSubmit.bind(this), true);
          // Shop Pay / PayPal possono montarsi dopo: osserviamo e ri-applichiamo il gate.
          this.paymentObserver = new MutationObserver(() => {
            this.ensureCheckoutGate();
            this.syncCheckoutGate();
            this.syncSubmitButtons(Boolean(this.saved && this.getCharCount() > 0));
          });
          this.paymentObserver.observe(this.form, { childList: true, subtree: true });
        }
      }

      // Sticky ATC e altri submit esterni al form: blocco click se ricamo obbligatorio.
      this.onLockedSubmitClick = this.onLockedSubmitClick.bind(this);
      if (this.required) {
        document.addEventListener('click', this.onLockedSubmitClick, true);
        this.syncSubmitButtons(false);
        this.syncCheckoutGate();
      }

      this.onKeydown = (event) => {
        if (this.isProcessing) return;
        if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
          this.cancelAndClose();
        }
      };
      document.addEventListener('keydown', this.onKeydown);

      this.openBtn?.addEventListener('click', () => this.openDrawer());
      this.editBtn?.addEventListener('click', () => this.openDrawer());
      this.shareBtn?.addEventListener('click', () => this.shareCustomization());
      this.closeBtn?.addEventListener('click', () => this.cancelAndClose());
      this.overlay?.addEventListener('click', () => this.cancelAndClose());
      this.saveBtn?.addEventListener('click', () => this.saveCustomization());

      this.messageInput?.addEventListener('input', () => this.onMessageInput());
      this.noteInput?.addEventListener('input', () => {
        this.syncNoteProperty();
        this.markDirty();
      });

      this.controlAll('[data-customization-position]').forEach((input) => {
        input.addEventListener('change', () => this.updatePreviewState());
      });
      this.controlAll('[data-customization-style]').forEach((input) => {
        input.addEventListener('change', () => this.updatePreviewState());
      });
      this.controlAll('[data-customization-color]').forEach((input) => {
        input.addEventListener('change', () => this.updatePreviewState());
      });
      this.controlAll('[data-customization-product-color]').forEach((input) => {
        input.addEventListener('change', () => this.onProductColorChange(input));
      });

      if (typeof subscribe === 'function') {
        this.variantChangeUnsubscriber = subscribe(
          typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.variantChange : 'variant-change',
          (event) => {
            const variant = event.data?.variant ?? null;
            this.selectedVariant = variant;
            this._productVariants = null;

            if (variant?.id && this._pendingVariantId && String(variant.id) === String(this._pendingVariantId)) {
              this._pendingVariantId = null;
              this._pendingDrawerColor = null;
            }

            if (variant?.price != null) {
              this.basePriceCents = variant.price;
              if (this.saved) {
                this.updateTotalPriceDisplay();
              }
            }

            // Cambiare taglia/colore/fit non invalida la personalizzazione già
            // salvata: l'utente può correggere la variante e riprovare l'aggiunta.
            this.syncProductColorAvailability();
            if (this._pendingOptionQueue?.length) {
              this.flushPendingOptionQueue();
              this.updatePreviewImages();
              this.applyThreadColorRestrictions();
              this.updatePreviewState(false);
              if (this.saved) this.setAddToCartState(true);
              return;
            }
            if (!variant && this._pendingDrawerColor) {
              if (this._pendingVariantId) {
                this.updatePreviewImages();
                this.applyThreadColorRestrictions();
                this.updatePreviewState(false);
                if (this.saved) this.setAddToCartState(true);
                return;
              }
              this.revertProductColorSelection();
              this._pendingDrawerColor = null;
              this._pendingVariantId = null;
            } else {
              this._pendingDrawerColor = null;
              if (variant) this.rememberConfirmedProductColor(variant);
              this.syncProductColorFromVariant(variant);
            }
            this.updatePreviewImages();
            this.applyThreadColorRestrictions();
            this.updatePreviewState(false);
            if (this.saved) this.setAddToCartState(true);
          }
        );

        this.cartUpdateUnsubscriber = subscribe(
          typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.cartUpdate : 'cart-update',
          (event) => {
            if (event.source === 'cart-reset') {
              this.cartPersonalizationId = null;
            }
          }
        );

        this.cartErrorUnsubscriber = subscribe(
          typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.cartError : 'cart-error',
          (event) => {
            if (event.source !== 'product-form' || !this.savedSnapshot) return;
            this.preserveSavedStateAfterFailedAdd();
          }
        );
      }

      const initialVariant = this.getUiSelectedVariant() || this.getSelectedVariantRecord();
      if (initialVariant) {
        this.selectedVariant = initialVariant;
        this.rememberConfirmedProductColor(initialVariant);
      }

      this.syncProductColorFromVariant(this.selectedVariant);
      this.syncProductColorAvailability();
      this.updatePreviewImages();
      this.applyThreadColorRestrictions();
      this.updatePreviewState(false);
      this.autoGrowMessage();
      this.updatePricing();
      this.syncNoteProperty();
      this.setAddToCartState(false);
      this.updateSavedUI();
      this.portalToBody();
      this.autoGrowMessage();
      this.onEditPersonalizationClick = this.onEditPersonalizationClick.bind(this);
      document.addEventListener('click', this.onEditPersonalizationClick, true);
      if (!this.adoptLiveEditFromPreviousInstance()) {
        this.initEditModeFromUrl();
      }
    }

    // Sposta overlay e drawer direttamente in <body> per evitare che uno
    // stacking context di un antenato (es. .product-grid__sticky, z-index:2)
    // li faccia finire sotto l'header. Le query dei controlli usano this.control().
    portalToBody() {
      if (this.overlay && this.overlay.parentElement !== document.body) {
        document.body.appendChild(this.overlay);
      }
      if (this.drawer && this.drawer.parentElement !== document.body) {
        document.body.appendChild(this.drawer);
      }
    }

    disconnectedCallback() {
      this.persistLiveEditState();
      this.variantChangeUnsubscriber?.();
      this.cartUpdateUnsubscriber?.();
      this.cartErrorUnsubscriber?.();
      this.paymentObserver?.disconnect();
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      if (this.onLockedSubmitClick) {
        document.removeEventListener('click', this.onLockedSubmitClick, true);
      }
      if (this.onEditPersonalizationClick) {
        document.removeEventListener('click', this.onEditPersonalizationClick, true);
      }
      document.body.classList.remove('product-customization-open');
      this.overlay?.remove();
      this.drawer?.remove();
    }

    persistLiveEditState() {
      if (!this.editMode && !this.drawer?.classList.contains('is-open')) return;

      window.__melideCustomizationLiveEdit = {
        productId: this.productId,
        editMode: this.editMode,
        cartPersonalizationId: this.cartPersonalizationId,
        cartEditLineKey: this.cartEditLineKey,
        cartEditFeeLineKey: this.cartEditFeeLineKey,
        cartEditVariantId: this.cartEditVariantId,
        cartEditQuantity: this.cartEditQuantity,
        saved: this.saved,
        snapshot: this.getFormSnapshot(),
        drawerWasOpen: Boolean(this.drawer?.classList.contains('is-open')),
      };
    }

    consumePersistedLiveEditState() {
      const state = window.__melideCustomizationLiveEdit;
      if (!state) return null;
      if (state.productId && this.productId && String(state.productId) !== String(this.productId)) {
        return null;
      }
      window.__melideCustomizationLiveEdit = null;
      return state;
    }

    applyLiveEditState(state, { reopenDrawer = false } = {}) {
      if (!state) return;

      this.editMode = Boolean(state.editMode);
      this.cartPersonalizationId = state.cartPersonalizationId || null;
      this.cartEditLineKey = state.cartEditLineKey || null;
      this.cartEditFeeLineKey = state.cartEditFeeLineKey || null;
      this.cartEditVariantId = state.cartEditVariantId || null;
      this.cartEditQuantity = state.cartEditQuantity || 1;
      this.saved = Boolean(state.saved);

      if (state.snapshot) {
        this.savedSnapshot = state.snapshot;
        this.restoreSnapshotToFields();
        this.refreshSummaryFromFields();
      }

      if (this.hiddenLinkId && this.cartPersonalizationId) {
        this.hiddenLinkId.value = this.cartPersonalizationId;
      }

      if (this.editMode) {
        this.setSavedNotice('Stai modificando una personalizzazione già nel carrello.');
      }

      this.updateSavedUI();
      this.setAddToCartState(this.saved);

      const restoredColor = this.control('[data-customization-product-color]:checked')?.value;
      if (this.editMode && restoredColor && !this.optionValuesEqual(this.getPdpColorValue(), restoredColor)) {
        this.onProductColorChange(this.control('[data-customization-product-color]:checked'));
      }

      if (reopenDrawer && state.drawerWasOpen) {
        this.openDrawer();
      }
    }

    adoptLiveEditFromPreviousInstance() {
      const others = Array.from(document.querySelectorAll('product-customization')).filter(
        (element) => element !== this
      );
      const previous =
        others.find((element) => element.editMode || element.drawer?.classList.contains('is-open')) ||
        others[0];

      let state = null;
      if (previous && (previous.editMode || previous.drawer?.classList.contains('is-open'))) {
        previous.persistLiveEditState?.();
        state = this.consumePersistedLiveEditState();
        previous.editMode = false;
        previous.cartPersonalizationId = null;
        previous.closeDrawer?.();
        previous.overlay?.remove();
        previous.drawer?.remove();
      } else {
        state = this.consumePersistedLiveEditState();
      }

      if (!state || (!state.editMode && !state.drawerWasOpen)) return false;

      this.applyLiveEditState(state, { reopenDrawer: true });
      return true;
    }

    getPaymentButtonRoot() {
      if (!this.form) return null;
      return (
        this.form.querySelector('.shopify-payment-button') ||
        this.form.querySelector('[data-shopify="payment-button"]')
      );
    }

    getLinkedSubmitButtons() {
      const buttons = [];
      const seen = new Set();
      const push = (btn) => {
        if (!btn || seen.has(btn)) return;
        seen.add(btn);
        buttons.push(btn);
      };

      push(this.productForm?.querySelector('.product-form__submit'));
      push(this.productForm?.querySelector('[type="submit"][name="add"]'));

      const formId = this.form?.id;
      if (formId) {
        document
          .querySelectorAll(
            `button[type="submit"][form="${CSS.escape(formId)}"], .product-bar__submit[form="${CSS.escape(formId)}"]`
          )
          .forEach(push);
      }

      // Sticky cart della PDP (può non avere form= aggiornato subito).
      document.querySelectorAll('.product-bar__submit').forEach(push);

      return buttons;
    }

    syncSubmitButtons(enabled) {
      if (!this.required) return;

      this.getLinkedSubmitButtons().forEach((btn) => {
        if (btn.disabled) return;
        if (enabled) {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('is-customization-locked');
        } else {
          btn.setAttribute('aria-disabled', 'true');
          btn.classList.add('is-customization-locked');
        }
      });
    }

    onLockedSubmitClick(event) {
      if (!this.required) return;
      if (this.saved && this.getCharCount() > 0) return;

      const btn = event.target.closest(
        '.product-bar__submit, .product-form__submit, button[name="add"][type="submit"]'
      );
      if (!btn) return;

      const formId = this.form?.id;
      const linked =
        this.productForm?.contains(btn) ||
        btn.classList.contains('product-bar__submit') ||
        (formId && btn.getAttribute('form') === formId);
      if (!linked) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.openDrawer();
    }

    ensureCheckoutGate() {
      if (!this.required) return;

      const paymentRoot = this.getPaymentButtonRoot();
      if (!paymentRoot) return;

      if (getComputedStyle(paymentRoot).position === 'static') {
        paymentRoot.style.position = 'relative';
      }

      this.checkoutGate = paymentRoot.querySelector('[data-customization-checkout-gate]');
      if (!this.checkoutGate) {
        this.checkoutGate = document.createElement('button');
        this.checkoutGate.type = 'button';
        this.checkoutGate.setAttribute('data-customization-checkout-gate', '');
        this.checkoutGate.className = 'product-customization__checkout-gate';
        this.checkoutGate.setAttribute(
          'aria-label',
          'Salva la personalizzazione prima di procedere con il pagamento rapido'
        );
        this.checkoutGate.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.openDrawer();
        });
        paymentRoot.appendChild(this.checkoutGate);
      }
    }

    syncCheckoutGate() {
      // Shop Pay / PayPal non passano dal submit: con ricamo obbligatorio li
      // nascondiamo finché la personalizzazione non è salvata.
      if (!this.required) {
        if (this.checkoutGate) this.checkoutGate.hidden = true;
        this.form?.classList.remove('product-form--customization-incomplete');
        this.productForm?.classList.remove('product-form--customization-incomplete');
        return;
      }

      this.ensureCheckoutGate();
      const ready = Boolean(this.saved && this.getCharCount() > 0);
      this.form?.classList.toggle('product-form--customization-incomplete', !ready);
      this.productForm?.classList.toggle('product-form--customization-incomplete', !ready);

      const paymentRoot = this.getPaymentButtonRoot();
      if (paymentRoot) {
        paymentRoot.hidden = !ready;
        paymentRoot.setAttribute('aria-hidden', ready ? 'false' : 'true');
      }

      if (this.checkoutGate) {
        this.checkoutGate.hidden = ready;
        this.checkoutGate.tabIndex = ready ? -1 : 0;
      }
    }

    isEmbroideryFeeItem(item) {
      if (typeof isEmbroideryFeeCartItem === 'function') {
        return isEmbroideryFeeCartItem(item);
      }
      const properties = item?.properties || {};
      if (properties._is_embroidery_fee === '1') return true;
      if (item?.handle === 'supplemento-ricamo-personalizzato') return true;
      if (typeof isTaggedFeeProduct === 'function' && properties._is_addon_fee !== '1') {
        return isTaggedFeeProduct(item);
      }
      return false;
    }

    async fetchCart() {
      const response = await fetch(`${window.routes.cart_url}.js`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error('Impossibile leggere il carrello.');
      }

      return response.json();
    }

    findCartItemByPersonalizationId(items = [], personalizationId) {
      if (!personalizationId) return null;

      return items.find((item) => {
        if (this.isEmbroideryFeeItem(item)) return false;
        return item.properties?._personalization_id === personalizationId;
      });
    }

    isPersonalizedMainItem(item) {
      if (!item || this.isEmbroideryFeeItem(item)) return false;
      const props = item.properties || {};
      return (
        hasEmbroideryText(props) ||
        Boolean(getEmbroideryProp(props, 'text', this.propertyLabels))
      );
    }

    findPersonalizedCartItemForThisProduct(items = []) {
      const matches = (items || []).filter((item) => {
        if (!this.isPersonalizedMainItem(item)) return false;
        if (this.productId && String(item.product_id) !== String(this.productId)) {
          return false;
        }
        return true;
      });

      if (!matches.length) return null;
      if (matches.length === 1) return matches[0];

      const variantId = this.getSelectedVariantId();
      if (variantId) {
        const byVariant = matches.find(
          (item) => String(item.variant_id || item.id) === String(variantId)
        );
        if (byVariant) return byVariant;
      }

      return matches[0];
    }

    async isPersonalizationInCart(personalizationId) {
      if (!personalizationId) return false;

      const cart = await this.fetchCart();
      return cart.items.some(
        (item) => item.properties?._personalization_id === personalizationId
      );
    }

    async removePersonalizationFromCart(personalizationId) {
      const cart = await this.fetchCart();
      const updates = {};

      cart.items.forEach((item) => {
        if (item.properties?._personalization_id === personalizationId) {
          updates[item.key] = 0;
        }
      });

      if (!Object.keys(updates).length) return null;

      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ updates }),
      });

      const data = await response.json();
      if (data.status) {
        throw new Error(data.description || data.message || 'Cart error');
      }

      return data;
    }

    async removeStalePersonalizationLines(personalizationId, keepMainVariantId, keepFeeVariantId, keepFeeQty) {
      if (!personalizationId) return null;

      const cart = await this.fetchCart();
      const updates = {};
      const feeKeepers = [];

      cart.items.forEach((item) => {
        if (item.properties?._personalization_id !== personalizationId) return;

        const variantId = item.variant_id || item.id;
        if (this.isEmbroideryFeeItem(item)) {
          if (keepFeeVariantId && String(variantId) === String(keepFeeVariantId)) {
            feeKeepers.push(item);
            return;
          }
          updates[item.key] = 0;
          return;
        }

        if (keepMainVariantId && String(variantId) === String(keepMainVariantId)) return;
        updates[item.key] = 0;
      });

      if (feeKeepers.length > 1) {
        const preferred =
          feeKeepers.find((item) => keepFeeQty && item.quantity === keepFeeQty) || feeKeepers[0];
        feeKeepers.forEach((item) => {
          if (item.key !== preferred.key) updates[item.key] = 0;
        });
      }

      if (!Object.keys(updates).length) return null;

      const response = await this.postCartJson(window.routes.cart_update_url, { updates });
      const data = await response.json();
      if (data.status) {
        throw new Error(data.description || data.message || 'Cart error');
      }
      return data;
    }

    selectOption(selector, value) {
      if (!value) return;

      const wanted = String(value).toLowerCase();
      const input = Array.from(this.controlAll(selector)).find((element) => {
        return (
          element.value?.toLowerCase() === wanted ||
          element.dataset.label?.toLowerCase() === wanted
        );
      });

      // In revert/sync il fallback può essere il colore corrente anche se
      // quella combinazione è esaurita: va comunque spuntato, altrimenti
      // resta selezionato il radio non valido cliccato nel drawer.
      if (input && (this._syncingProductColor || !input.disabled)) {
        input.checked = true;
      }
    }

    restoreFromCartProperties(properties) {
      const labels = this.propertyLabels;
      const message = decodeLineBreaks(getEmbroideryProp(properties, 'text', labels) || '');
      const style = getEmbroideryProp(properties, 'font', labels) || '';
      const position = getEmbroideryProp(properties, 'position', labels) || '';
      const color = getEmbroideryProp(properties, 'color', labels) || '';
      const note = getEmbroideryProp(properties, 'notes', labels) || '';
      const charCount =
        parseInt(properties[MELIDE_LETTERS_KEY] || '0', 10) || this.getCharCount();
      const price =
        getEmbroideryProp(properties, 'cost', labels) ||
        this.formatMoney(charCount * this.charPrice);

      if (this.messageInput) {
        this.messageInput.value = message;
        this.autoGrowMessage();
      }

      if (this.noteInput) {
        this.noteInput.value = note;
        this.syncNoteProperty();
      }

      this.selectOption('[data-customization-position]', position);
      this.selectOption('[data-customization-style]', style);
      this.selectOption('[data-customization-color]', color);

      if (this.hiddenLinkId && properties._personalization_id) {
        this.hiddenLinkId.value = properties._personalization_id;
      }

      if (this.summaryText) this.summaryText.textContent = message;
      if (this.summaryStyle) this.summaryStyle.textContent = style || '—';
      if (this.summaryPosition) this.summaryPosition.textContent = position || '—';
      if (this.summaryColor) this.summaryColor.textContent = color || '—';
      if (this.summaryNote) this.summaryNote.textContent = note || '—';
      if (!this.feeIncluded && this.summaryPrice) {
        this.summaryPrice.textContent = price;
      }
      if (this.hiddenCharCount) this.hiddenCharCount.value = String(charCount);
      if (!this.feeIncluded && this.hiddenPrice) {
        this.hiddenPrice.value = price;
      }

      this.summaryEl?.classList.remove('is-empty');
      this.saved = true;
      this.savedSnapshot = this.getFormSnapshot();
      this.setAddToCartState(true);
      this.updatePreviewState(false);
      this.updatePricing();
      this.updateSavedUI();
    }

    getEditPersonalizationIdFromUrl() {
      try {
        const params = new URLSearchParams(window.location.search);
        return params.get('edit_personalization');
      } catch (error) {
        return null;
      }
    }

    onEditPersonalizationClick(event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target.closest?.('a.cart-item__edit-personalization');
      if (!link) return;

      let url;
      try {
        url = new URL(link.href, window.location.origin);
      } catch (error) {
        return;
      }

      const editId = url.searchParams.get('edit_personalization');
      if (!editId) return;

      const productPath = this.dataset.productUrl || '';
      const sameProduct =
        url.pathname === window.location.pathname ||
        (productPath && url.pathname === productPath);
      if (!sameProduct) return;

      // Già sulla PDP: niente reload. Il secondo "Modifica" altrimenti non
      // rimonta product-customization e il form resta quello dopo resetForm.
      event.preventDefault();
      event.stopPropagation();
      document.querySelector('cart-drawer')?.close?.();
      void this.beginCartEdit(editId);
    }

    // Il binding a una riga del carrello avviene SOLO quando si arriva dalla
    // pagina/tendina carrello tramite il link "Modifica" (?edit_personalization=ID).
    // In tutti gli altri casi ogni "Aggiungi al carrello" crea una riga
    // indipendente e il form viene ripulito dopo l'aggiunta.
    async initEditModeFromUrl() {
      const editId = this.getEditPersonalizationIdFromUrl();
      if (!editId) {
        // Visita PDP normale: nessuno stato edit o personalizzazione fantasma.
        this.editMode = false;
        this.cartPersonalizationId = null;
        return;
      }

      await this.beginCartEdit(editId);
    }

    async beginCartEdit(editId) {
      if (!editId || this.isProcessing || melideCustomizationSaveLock) return;

      try {
        const cart = await this.fetchCart();
        let match = this.findCartItemByPersonalizationId(cart.items, editId);

        // Il link "Modifica" può avere un id vecchio se l'HTML del drawer
        // non si è aggiornato: in quel caso usa la riga attuale di questo PDP.
        if (!this.isPersonalizedMainItem(match)) {
          match = this.findPersonalizedCartItemForThisProduct(cart.items);
        }

        if (!this.isPersonalizedMainItem(match)) {
          this.clearEditUrlParam();
          this.resetForm({ keepDrawerClosed: true });
          return;
        }

        // La modifica da carrello vale solo sul prodotto corretto.
        if (this.productId && String(match.product_id) !== String(this.productId)) {
          this.clearEditUrlParam();
          this.resetForm({ keepDrawerClosed: true });
          return;
        }

        this.editMode = true;
        this.cartPersonalizationId = match.properties?._personalization_id || editId;
        this.cartEditLineKey = match.key || null;
        this.cartEditVariantId = match.variant_id || match.id || null;
        this.cartEditQuantity = match.quantity || 1;
        const feeLine = (cart.items || []).find(
          (item) =>
            this.isEmbroideryFeeItem(item) &&
            item.properties?._personalization_id === this.cartPersonalizationId
        );
        this.cartEditFeeLineKey = feeLine?.key || null;
        this._restoringFromCart = true;
        this.restoreFromCartProperties(match.properties);
        const colorIndex = parseInt(this.dataset.colorOptionIndex || '-1', 10);
        const cartColor = colorIndex >= 0 ? match.options?.[colorIndex] : '';
        if (cartColor) {
          this._syncingProductColor = true;
          this.selectOption('[data-customization-product-color]', cartColor);
          this._syncingProductColor = false;
        }
        this.applyCartItemVariant(match);
        this.setSavedNotice('Stai modificando una personalizzazione già nel carrello.');
        this.openDrawer();
        this.setProcessingState(true, {
          message: 'Caricamento personalizzazione…',
          context: 'save',
        });
        await this.waitForPendingVariant();
        if (!this.cartItemVariantIsLive(match)) {
          this.applyCartItemVariant(match);
          await this.waitForPendingVariant();
        }
        this._restoringFromCart = false;
        this.applyThreadColorRestrictions();
        const restoredColor = getEmbroideryProp(match.properties || {}, 'color', this.propertyLabels);
        if (restoredColor) this.selectOption('[data-customization-color]', restoredColor);
        this.clearEditUrlParam();
      } catch (error) {
        console.error(error);
        this._restoringFromCart = false;
        this._pendingDrawerColor = null;
        this._pendingVariantId = null;
        this.clearEditUrlParam();
        this.resetForm({ keepDrawerClosed: true });
      } finally {
        if (this.isProcessing) this.setProcessingState(false);
      }
    }

    applyCartItemVariant(item) {
      if (!Array.isArray(item?.options) || !item.options.length) return;

      const current = this.getPdpSelectedOptionValues();
      const alreadySelected =
        current.length === item.options.length &&
        current.every((value, index) => this.optionValuesEqual(value, item.options[index]));
      const targetVariantId = item.variant_id || item.id || null;
      if (alreadySelected && (!targetVariantId || String(this.getSelectedVariantId()) === String(targetVariantId))) {
        this._pendingDrawerColor = null;
        this._pendingVariantId = null;
        return;
      }

      const colorIndex = parseInt(this.dataset.colorOptionIndex || '-1', 10);
      const cartColor = colorIndex >= 0 ? item.options[colorIndex] : '';
      if (cartColor) this._pendingDrawerColor = cartColor;
      this._pendingVariantId = targetVariantId;
      this._pendingOptionQueue = [];

      // NERO/XS → GRIGIO/XL non ha un passo intermedio disponibile
      // (GRIGIO/XS e NERO/XL sono esauriti). Prima atterriamo su un colore
      // disponibile, poi la coda applica taglia/fit della riga carrello.
      const best = cartColor ? this.findBestAvailableVariantForColor(cartColor) : undefined;
      const needsColorJump =
        Boolean(best?.id) &&
        cartColor &&
        !this.optionValuesEqual(this.getPdpColorValue(), cartColor) &&
        String(best.id) !== String(this.getSelectedVariantId());

      if (needsColorJump) {
        this._pendingOptionQueue = this.diffVariantOptions({ options: item.options });
        if (this.setPdpOptionsTogether(best.options, targetVariantId)) return;
      }

      if (this.setPdpOptionsTogether(item.options, targetVariantId)) return;

      this._pendingOptionQueue = this.diffVariantOptions({ options: item.options });
      const first = this._pendingOptionQueue.shift();
      if (first && !this.setPdpOptionByIndex(first.index, first.value)) {
        this._pendingOptionQueue = [];
        this._pendingDrawerColor = null;
        this._pendingVariantId = null;
      }
    }

    setPdpOptionsTogether(optionValues, variantId) {
      const controls = this.getPdpOptionControls();
      if (!Array.isArray(optionValues) || !optionValues.length) return false;
      if (controls.length < optionValues.length) return false;

      let lastControl = null;
      for (let index = 0; index < optionValues.length; index += 1) {
        const control = controls[index];
        const value = optionValues[index];
        if (!control || !value) return false;

        if (control.tagName === 'SELECT') {
          const option = Array.from(control.options).find((item) =>
            this.optionValuesEqual(item.value, value)
          );
          if (!option) return false;
          Array.from(control.options).forEach((item) => item.removeAttribute('selected'));
          option.setAttribute('selected', 'selected');
          control.value = option.value;
          lastControl = control;
        } else {
          const radio = Array.from(control.querySelectorAll('input[type="radio"]')).find((input) =>
            this.optionValuesEqual(input.value, value)
          );
          if (!radio) return false;
          radio.checked = true;
          lastControl = radio;
        }
      }

      this._pendingOptionQueue = [];
      this._pendingVariantId = variantId || this._pendingVariantId;
      lastControl.dispatchEvent(new Event('change', { bubbles: true }));
      this.getLiveVariantSelects()?.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    cartItemVariantIsLive(item) {
      if (!item) return false;
      const liveId = this.getSelectedVariantId();
      const targetId = item.variant_id || item.id;
      if (targetId && liveId && String(liveId) === String(targetId)) return true;
      const liveOptions = this.getPdpSelectedOptionValues();
      return (
        Array.isArray(item.options) &&
        item.options.length === liveOptions.length &&
        item.options.every((value, index) => this.optionValuesEqual(value, liveOptions[index]))
      );
    }

    setSavedNotice(message) {
      if (!this.savedNotice) return;
      this.savedNotice.textContent = message || this.defaultSavedNoticeText;
    }

    openDrawer() {
      if (this.isProcessing && !this._restoringFromCart) return;

      // Aprire il drawer per una nuova personalizzazione (non edit da carrello)
      // non deve trascinare binding a righe carrello precedenti.
      if (!this.editMode) {
        this.cartPersonalizationId = null;
      }
      // Durante restore/cambio colore non sovrascrivere lo swatch del drawer
      // con la variante PDP ancora vecchia (resta visibile 500ms nel morph).
      if (!this._restoringFromCart && !this._pendingDrawerColor && !this._pendingOptionQueue?.length) {
        this.syncProductColorFromVariant(this.selectedVariant || this.getSelectedVariantRecord());
      }
      this.syncProductColorAvailability();
      this.updatePreviewImages();
      if (!this._restoringFromCart) {
        this.applyThreadColorRestrictions();
      }
      this.updatePreviewState(false);
      this.overlay?.classList.add('is-open');
      this.drawer?.classList.add('is-open');
      document.body.classList.add('product-customization-open');
    }

    closeDrawer() {
      this.overlay?.classList.remove('is-open');
      this.drawer?.classList.remove('is-open');
      document.body.classList.remove('product-customization-open');
    }

    // X fluttuante / overlay / Esc: annulla le modifiche non salvate e chiude.
    // - In edit mode (arrivo dal carrello con ?edit_personalization) annullare
    //   significa ABBANDONARE la modifica: reset completo, così non resta un
    //   binding "fantasma" alla riga carrello che potrebbe far duplicare o
    //   sostituire per errore all'aggiunta successiva.
    // - Fuori da edit mode, se esiste uno stato salvato lo ripristina (non perde
    //   la personalizzazione già salvata); altrimenti svuota i campi.
    // La chiusura è animata via CSS.
    cancelAndClose() {
      if (this.isProcessing) return;

      if (this.editMode) {
        this.resetForm();
      } else if (this.savedSnapshot) {
        this.restoreSnapshotToFields();
        this.hideValidationError();
      } else {
        this.resetForm();
      }
      this.closeDrawer();
    }

    restoreSnapshotToFields() {
      if (!this.savedSnapshot) return;

      let snapshot;
      try {
        snapshot = JSON.parse(this.savedSnapshot);
      } catch (error) {
        return;
      }

      if (this.messageInput) this.messageInput.value = snapshot.message || '';
      this.autoGrowMessage();
      if (this.noteInput) this.noteInput.value = snapshot.note || '';
      this.selectOption('[data-customization-position]', snapshot.position);
      this.selectOption('[data-customization-style]', snapshot.style);
      this.selectOption('[data-customization-color]', snapshot.color);
      if (snapshot.productColor) {
        this._syncingProductColor = true;
        this.selectOption('[data-customization-product-color]', snapshot.productColor);
        this._syncingProductColor = false;
      }

      this.updatePreviewState(false);
      this.updatePricing();
    }

    getMessageValue() {
      return (this.messageInput?.value || '').trim();
    }

    // Il campo testo parte da una riga e cresce quando l'utente va a capo.
    autoGrowMessage() {
      const el = this.messageInput;
      if (!el || el.tagName !== 'TEXTAREA') return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }

    updatePreviewTextContent() {
      if (!this.previewText) return;
      this.previewText.textContent = this.getMessageValue();
    }

    getCharCount() {
      return countableCharLength(this.getMessageValue());
    }

    getSelectedGarmentColorSlug() {
      const checked = this.control('[data-customization-product-color]:checked');
      if (checked) {
        return checked.dataset.colorSlug || slugifyColorName(checked.value);
      }

      const index = parseInt(this.dataset.colorOptionIndex || '-1', 10);
      const variant = this.selectedVariant || this.getSelectedVariantRecord();
      if (index >= 0 && variant?.options?.[index]) {
        return slugifyColorName(variant.options[index]);
      }

      return slugifyColorName(this.dataset.productColor || '');
    }

    updatePreviewImages() {
      const garment = this.garmentType || this.dataset.garmentType || 'tshirt';
      let slug = this.getSelectedGarmentColorSlug();
      if (!slug && garment === 'borsone') slug = 'naturale';
      const garmentMap = this.previewMap?.[garment] || {};
      const colorMap = garmentMap[slug] || {};
      const tshirtFallback = this.previewMap?.tshirt?.bianco || {};
      const firstGarment = Object.values(garmentMap)[0] || {};

      this.previewImages?.forEach((image) => {
        const side = image.dataset.customizationPreviewImage;
        let url = colorMap[side] || colorMap.front || '';
        if (!url && slug === 'bianco') {
          url = tshirtFallback[side] || tshirtFallback.front || '';
        }
        if (!url) {
          url = firstGarment[side] || firstGarment.front || tshirtFallback[side] || tshirtFallback.front || '';
        }
        if (url && image.getAttribute('src') !== url) {
          image.src = url;
        }
      });
    }

    applyThreadColorRestrictions() {
      const forbidden = forbiddenThreadSlugs(
        this.getSelectedGarmentColorSlug(),
        this.garmentType || this.dataset.garmentType
      );
      const inputs = Array.from(this.controlAll('[data-customization-color]'));
      if (!inputs.length) return;

      const blockable = inputs.filter((input) => {
        const slug = input.dataset.colorSlug || slugifyThreadName(input.value);
        return forbidden.includes(slug);
      });

      // Non nascondere tutta la palette se resterebbe vuota.
      const hideBlocked = blockable.length > 0 && blockable.length < inputs.length;
      let checkedBecameInvalid = false;

      inputs.forEach((input) => {
        const slug = input.dataset.colorSlug || slugifyThreadName(input.value);
        const blocked = hideBlocked && forbidden.includes(slug);
        const option = input.closest('.product-customization__option');
        input.disabled = blocked;
        option?.toggleAttribute('hidden', blocked);
        if (blocked && input.checked) {
          checkedBecameInvalid = true;
          if (!this._restoringFromCart) {
            input.checked = false;
          }
        }
      });

      if (checkedBecameInvalid && !this._restoringFromCart) {
        const next = inputs.find((input) => !input.disabled);
        if (next) next.checked = true;
        if (this.saved) this.markDirty();
      }
    }

    onProductColorChange(input) {
      if (this._syncingProductColor || this.isProcessing) return;

      const nextColor = input?.value || '';
      if (!this.isProductColorSelectable(nextColor)) {
        this.revertProductColorSelection();
        return;
      }

      this._pendingDrawerColor = nextColor;
      this._pendingOptionQueue = [];
      this._pendingVariantId = null;

      const best = this.findBestAvailableVariantForColor(nextColor);
      let applied = false;

      if (best) {
        this._pendingVariantId = best.id || null;
        applied = this.setPdpOptionsTogether(best.options, best.id);
        if (!applied) {
          this._pendingOptionQueue = this.diffVariantOptions(best);
          const first = this._pendingOptionQueue.shift();
          applied = first
            ? this.setPdpOptionByIndex(first.index, first.value)
            : true;
        }
      } else {
        applied = this.setPdpColor(nextColor);
      }

      if (!applied) {
        this._pendingDrawerColor = null;
        this._pendingOptionQueue = [];
        this._pendingVariantId = null;
        this.revertProductColorSelection();
        return;
      }

      this.updatePreviewImages();
      this.applyThreadColorRestrictions();
      this.updatePreviewState(false);
    }

    rememberConfirmedProductColor(variant) {
      const index = parseInt(this.dataset.colorOptionIndex || '-1', 10);
      if (index >= 0 && variant?.options?.[index]) {
        this._confirmedProductColor = variant.options[index];
        return;
      }
      this._confirmedProductColor = this.getPdpColorValue() || this._confirmedProductColor;
    }

    revertProductColorSelection() {
      this._pendingDrawerColor = null;
      this._pendingOptionQueue = [];
      this._pendingVariantId = null;
      const fallback =
        this._confirmedProductColor ||
        this.getPdpColorValue() ||
        this.control('[data-customization-product-color]:not(:disabled)')?.value ||
        '';
      this._syncingProductColor = true;
      this.selectOption('[data-customization-product-color]', fallback);
      this._syncingProductColor = false;
      this.syncProductColorAvailability();
      this.updatePreviewImages();
      this.applyThreadColorRestrictions();
      this.updatePreviewState(false);
    }

    getLiveVariantSelects() {
      const nodes = Array.from(document.querySelectorAll('product-variant-selects'));
      const visible = nodes.find((el) => {
        if (el.style.display === 'none') return false;
        const info = el.closest('product-info');
        if (info && info.style.display === 'none') return false;
        return true;
      });
      return visible || nodes.find((el) => el.style.display !== 'none') || nodes[0] || null;
    }

    getPdpSelectedOptionValues() {
      const root = this.getLiveVariantSelects();
      if (!root) return [];

      const values = [];
      root.querySelectorAll('select, fieldset').forEach((el) => {
        if (el.tagName === 'SELECT') {
          values.push(el.value || '');
          return;
        }
        const checked = el.querySelector('input:checked');
        if (checked) values.push(checked.value || '');
      });
      return values;
    }

    optionValuesEqual(a, b) {
      return String(a || '').toLowerCase() === String(b || '').toLowerCase();
    }

    getSizeOptionIndex() {
      const root = this.getLiveVariantSelects();
      if (!root) return -1;
      const names = Array.from(root.querySelectorAll('[data-option-name]')).map(
        (el) => el.dataset.optionName || ''
      );
      return names.findIndex((name) => /taglia|size|taille|talla|größe/.test(name.toLowerCase()));
    }

    getPdpOptionControls() {
      const root = this.getLiveVariantSelects();
      if (!root) return [];
      return Array.from(root.querySelectorAll('select, fieldset'));
    }

    findBestAvailableVariantForColor(colorName) {
      const colorIndex = parseInt(this.dataset.colorOptionIndex || '-1', 10);
      const variants = this.getProductVariants();
      if (!Number.isInteger(colorIndex) || colorIndex < 0 || !variants.length) {
        return undefined;
      }

      let currentOptions = this.getPdpSelectedOptionValues();
      if (!currentOptions.length) {
        const current = this.getSelectedVariantRecord() || this.getUiSelectedVariant();
        if (current?.options?.length) currentOptions = [...current.options];
      }
      if (!currentOptions.length || colorIndex >= currentOptions.length) return undefined;

      const withColor = variants.filter(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === currentOptions.length &&
          this.optionValuesEqual(variant.options[colorIndex], colorName)
      );
      const available = withColor.filter((variant) => variant.available !== false);
      if (!available.length) return null;

      const exact = available.find((variant) =>
        variant.options.every((option, index) => this.optionValuesEqual(option, currentOptions[index]))
      );
      if (exact) return exact;

      const sizeIndex = this.getSizeOptionIndex();
      if (sizeIndex >= 0) {
        const sameOthers = available.filter((variant) =>
          variant.options.every(
            (option, index) =>
              index === sizeIndex || this.optionValuesEqual(option, currentOptions[index])
          )
        );
        if (sameOthers.length) {
          return this.pickClosestSizeVariant(sameOthers, sizeIndex, currentOptions[sizeIndex]);
        }
      }

      available.sort((a, b) => {
        const score = (variant) =>
          variant.options.reduce(
            (count, option, index) =>
              count + (this.optionValuesEqual(option, currentOptions[index]) ? 1 : 0),
            0
          );
        return score(b) - score(a);
      });
      return available[0];
    }

    pickClosestSizeVariant(variants, sizeIndex, currentSize) {
      const controls = this.getPdpOptionControls();
      const sizeSelect = controls[sizeIndex];
      const order = sizeSelect
        ? Array.from(sizeSelect.options || []).map((option) => option.value)
        : [];
      const currentPos = order.findIndex((value) => this.optionValuesEqual(value, currentSize));

      return [...variants].sort((a, b) => {
        const posA = order.findIndex((value) => this.optionValuesEqual(value, a.options[sizeIndex]));
        const posB = order.findIndex((value) => this.optionValuesEqual(value, b.options[sizeIndex]));
        const distA = posA < 0 ? 99 : Math.abs(posA - Math.max(currentPos, 0));
        const distB = posB < 0 ? 99 : Math.abs(posB - Math.max(currentPos, 0));
        return distA - distB;
      })[0];
    }

    diffVariantOptions(variant) {
      const current = this.getPdpSelectedOptionValues();
      const colorIndex = parseInt(this.dataset.colorOptionIndex || '-1', 10);
      if (!variant?.options?.length) return [];

      const diffs = variant.options
        .map((value, index) =>
          this.optionValuesEqual(value, current[index]) ? null : { index, value }
        )
        .filter(Boolean);

      diffs.sort((a, b) => {
        if (a.index === colorIndex) return 1;
        if (b.index === colorIndex) return -1;
        return a.index - b.index;
      });
      return diffs;
    }

    flushPendingOptionQueue() {
      const next = this._pendingOptionQueue.shift();
      if (!next) return;
      const applied = this.setPdpOptionByIndex(next.index, next.value);
      if (!applied) {
        this._pendingOptionQueue = [];
        this._pendingDrawerColor = null;
        this._pendingVariantId = null;
        this.revertProductColorSelection();
      }
    }

    setPdpOptionByIndex(index, value) {
      const control = this.getPdpOptionControls()[index];
      if (!control || !value) return false;

      if (control.tagName === 'SELECT') {
        if (this.optionValuesEqual(control.value, value)) return true;
        const option = Array.from(control.options).find((item) =>
          this.optionValuesEqual(item.value, value)
        );
        if (!option || option.disabled) return false;
        const button = control
          .closest('dropdown-input')
          ?.querySelector(`button[data-value="${CSS.escape(option.value)}"]`);
        if (button && !button.disabled) {
          button.click();
          return true;
        }
        control.value = option.value;
        option.setAttribute('selected', 'selected');
        control.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      const radio = Array.from(control.querySelectorAll('input[type="radio"]')).find((input) =>
        this.optionValuesEqual(input.value, value)
      );
      if (!radio || radio.disabled) return false;
      if (!radio.checked) radio.click();
      return true;
    }

    findVariantForColor(colorName) {
      return this.findBestAvailableVariantForColor(colorName);
    }

    findPdpColorControl(colorName) {
      const root = this.getLiveVariantSelects();
      if (!root || !colorName) return null;

      const optionName = (this.dataset.colorOptionName || '').toLowerCase();
      const wanted = colorName.toLowerCase();

      const radio = Array.from(root.querySelectorAll('input[type="radio"]')).find((el) => {
        return this.isColorOptionControl(el, optionName) && el.value.toLowerCase() === wanted;
      });
      if (radio) {
        return {
          type: 'radio',
          input: radio,
          option: null,
          button: null,
          disabled: Boolean(radio.disabled || radio.classList.contains('disabled')),
        };
      }

      const select = Array.from(root.querySelectorAll('select')).find((el) =>
        this.isColorOptionControl(el, optionName)
      );
      if (!select) return null;

      const option = Array.from(select.options).find((item) => item.value.toLowerCase() === wanted);
      if (!option) return null;

      const dropdown = select.closest('dropdown-input');
      const button = dropdown?.querySelector(`button[data-value="${CSS.escape(option.value)}"]`) || null;

      return {
        type: 'select',
        input: select,
        option,
        button,
        disabled: Boolean(option.disabled || button?.disabled),
      };
    }

    isProductColorSelectable(colorName) {
      if (!colorName) return false;
      const control = this.findPdpColorControl(colorName);
      if (!control) return false;
      if (control.disabled) return false;

      const best = this.findBestAvailableVariantForColor(colorName);
      if (best === undefined) return true;
      return Boolean(best);
    }

    syncProductColorAvailability() {
      const inputs = Array.from(this.controlAll('[data-customization-product-color]'));
      if (!inputs.length) return;

      const current = (
        this._confirmedProductColor ||
        this.getPdpColorValue() ||
        this.control('[data-customization-product-color]:checked')?.value ||
        ''
      ).toLowerCase();

      inputs.forEach((input) => {
        const selectable = this.isProductColorSelectable(input.value);
        const isCurrent = input.value.toLowerCase() === current;
        const option = input.closest('.product-customization__option');
        input.disabled = !selectable && !isCurrent;
        option?.classList.toggle('is-unavailable', !selectable);
        const label = option?.querySelector('label');
        if (label) {
          const baseName = input.dataset.label || input.value;
          label.title = selectable ? baseName : `${baseName} — Non disponibile`;
        }
      });
    }

    syncProductColorFromVariant(variant) {
      if (this._pendingDrawerColor || this._pendingOptionQueue?.length) return;

      const index = parseInt(this.dataset.colorOptionIndex || '-1', 10);
      let colorName = '';
      if (index >= 0 && variant?.options?.[index]) {
        colorName = variant.options[index];
      } else {
        colorName = this.getPdpColorValue();
      }
      if (!colorName) return;

      this._syncingProductColor = true;
      this.selectOption('[data-customization-product-color]', colorName);
      this._syncingProductColor = false;
    }

    getPdpColorValue() {
      const root = this.getLiveVariantSelects();
      if (!root) return '';

      const optionName = (this.dataset.colorOptionName || '').toLowerCase();
      const radios = Array.from(root.querySelectorAll('input[type="radio"]:checked'));
      const radio = radios.find((el) => this.isColorOptionControl(el, optionName));
      if (radio) return radio.value;

      const select = Array.from(root.querySelectorAll('select')).find((el) =>
        this.isColorOptionControl(el, optionName)
      );
      return select?.value || '';
    }

    isColorOptionControl(el, optionName) {
      const name = (el.getAttribute('name') || el.id || '').toLowerCase();
      const legend = el.closest('fieldset')?.querySelector('legend')?.textContent?.toLowerCase() || '';
      const dropdownName = el.closest('.product-form__input')?.querySelector('[data-option-name]')?.dataset?.optionName?.toLowerCase() || '';
      if (optionName) {
        return name.includes(optionName) || legend.includes(optionName) || dropdownName === optionName;
      }
      return /colore|color|colour/.test(`${name} ${legend} ${dropdownName}`);
    }

    setPdpColor(colorName) {
      if (!colorName || !this.isProductColorSelectable(colorName)) return false;

      const control = this.findPdpColorControl(colorName);
      if (!control || control.disabled) return false;

      if (control.type === 'radio') {
        if (!control.input.checked) control.input.click();
        return true;
      }

      if (control.button && !control.button.disabled) {
        if (control.input.value !== control.option.value) {
          control.button.click();
        }
        return true;
      }

      return false;
    }

    getProductVariants() {
      const liveInfo = Array.from(document.querySelectorAll('product-info')).find(
        (el) => el.style.display !== 'none'
      );
      const script =
        liveInfo?.querySelector('[data-all-variants]') ||
        this.closest('product-info')?.querySelector('[data-all-variants]') ||
        document.querySelector('[data-all-variants]');

      if (!script?.textContent?.trim()) {
        this._productVariants = [];
        return this._productVariants;
      }

      try {
        this._productVariants = JSON.parse(script.textContent);
      } catch (error) {
        this._productVariants = [];
      }

      return this._productVariants;
    }

    getSelectedVariantId() {
      const liveInfo = Array.from(document.querySelectorAll('product-info')).find(
        (el) => el.style.display !== 'none'
      );
      const rawValue =
        liveInfo?.querySelector('form [name="id"]')?.value ||
        this.form?.querySelector('[name="id"]')?.value;
      const variantId = parseInt(rawValue, 10);
      return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
    }

    getSelectedVariantRecord() {
      const variantId = this.getSelectedVariantId();
      if (!variantId) return null;

      if (this.selectedVariant?.id === variantId) {
        return this.selectedVariant;
      }

      const fromMap = this.getProductVariants().find((variant) => variant.id === variantId);
      if (fromMap) return fromMap;

      const uiVariant = this.getUiSelectedVariant();
      if (uiVariant?.id === variantId) return uiVariant;

      return null;
    }

    getFallbackVariantFromForm() {
      const variantId = this.getSelectedVariantId();
      if (!variantId) return null;

      const fromMap = this.getProductVariants().find((variant) => variant.id === variantId);
      if (fromMap) return fromMap;

      const liveInfo = Array.from(document.querySelectorAll('product-info')).find(
        (el) => el.style.display !== 'none'
      );
      const idInput =
        liveInfo?.querySelector('form [name="id"]') || this.form?.querySelector('[name="id"]');

      return {
        id: variantId,
        available: idInput ? !idInput.disabled : true,
      };
    }

    getUiSelectedVariant() {
      if (this.selectedVariant?.id) {
        const liveId = this.getSelectedVariantId();
        if (liveId && this.selectedVariant.id === liveId) {
          return this.selectedVariant;
        }
      }

      const root = this.getLiveVariantSelects();
      const script =
        root?.querySelector('script[data-selected-variant]') ||
        this.closest('product-info')?.querySelector('script[data-selected-variant]') ||
        document.querySelector('product-variant-selects script[data-selected-variant]') ||
        document.querySelector('script[data-selected-variant]');

      if (script?.textContent?.trim()) {
        try {
          const parsed = JSON.parse(script.textContent);
          if (parsed?.id) return parsed;
        } catch (error) {
          // fall through: prodotti senza picker (bundle / Default Title)
        }
      }

      return this.getFallbackVariantFromForm();
    }

    getVariantUnavailableMessage() {
      return 'Questa combinazione di taglia, colore o fit non è disponibile. Scegli opzioni disponibili.';
    }

    getVariantSoldOutMessage() {
      return 'La taglia selezionata è esaurita in questo colore. Scegli un\'altra taglia disponibile e riprova.';
    }

    getCartAddErrorMessage(response, data = {}) {
      const raw = data.description || data.message || '';
      const normalized = String(raw).toLowerCase();

      if (/sold out|esaurit|out of stock|esaurita/i.test(normalized)) {
        return this.getVariantSoldOutMessage();
      }

      if (
        response?.status === 422 ||
        /unavailable|non disponibil|not available|cannot find variant|inventory|esiste/i.test(
          normalized
        )
      ) {
        return this.getVariantUnavailableMessage();
      }

      if (raw) return raw;

      return this.getVariantUnavailableMessage();
    }

    validateVariantForAdd() {
      const variantId = this.getSelectedVariantId();
      if (!variantId) {
        return {
          ok: false,
          message: this.getVariantUnavailableMessage(),
        };
      }

      const fromMap = this.getProductVariants().find((variant) => variant.id === variantId);
      const uiVariant = this.getUiSelectedVariant();
      const variant =
        fromMap ||
        (uiVariant?.id === variantId ? uiVariant : this.getFallbackVariantFromForm());

      if (!variant?.id) {
        return {
          ok: false,
          message: this.getVariantUnavailableMessage(),
        };
      }

      if (variant.available === false) {
        return {
          ok: false,
          message: this.getVariantSoldOutMessage(),
        };
      }

      const uiOptions = this.getPdpSelectedOptionValues();
      if (
        uiOptions.length &&
        Array.isArray(variant.options) &&
        variant.options.length === uiOptions.length &&
        !variant.options.every((option, index) => this.optionValuesEqual(option, uiOptions[index]))
      ) {
        return {
          ok: false,
          message: this.getVariantSoldOutMessage(),
        };
      }

      const exactUi = this.getProductVariants().find(
        (item) =>
          Array.isArray(item.options) &&
          item.options.length === uiOptions.length &&
          item.options.every((option, index) => this.optionValuesEqual(option, uiOptions[index]))
      );
      if (uiOptions.length && exactUi && exactUi.available === false) {
        return {
          ok: false,
          message: this.getVariantSoldOutMessage(),
        };
      }
      if (uiOptions.length && this.getProductVariants().length && !exactUi) {
        return {
          ok: false,
          message: this.getVariantUnavailableMessage(),
        };
      }

      return { ok: true, variant };
    }

    showCartError(message) {
      if (this.drawer?.classList.contains('is-open')) {
        this.showValidationError(message);
      }

      if (typeof this.productForm?.handleErrorMessage === 'function') {
        this.productForm.handleErrorMessage(message);
        return;
      }

      if (!this.drawer?.classList.contains('is-open')) {
        alert(message);
      }
    }

    clearCartError() {
      if (typeof this.productForm?.handleErrorMessage === 'function') {
        this.productForm.handleErrorMessage();
      }
    }

    refreshSummaryFromFields() {
      const message = this.getMessageValue();
      const charCount = this.getCharCount();
      const style = this.control('[data-customization-style]:checked')?.dataset.label || '';
      const position = this.control('[data-customization-position]:checked')?.dataset.label || '';
      const color = this.control('[data-customization-color]:checked')?.dataset.label || '';
      const note = (this.noteInput?.value || '').trim();
      const price = this.formatMoney(charCount * this.charPrice);

      if (this.summaryText) this.summaryText.textContent = message;
      if (this.summaryStyle) this.summaryStyle.textContent = style;
      if (this.summaryPosition) this.summaryPosition.textContent = position;
      if (this.summaryColor) this.summaryColor.textContent = color;
      if (this.summaryNote) this.summaryNote.textContent = note || '—';
      if (!this.feeIncluded && this.summaryPrice) {
        this.summaryPrice.textContent = price;
      }
      this.summaryEl?.classList.toggle('is-empty', !message);
    }

    preserveSavedStateAfterFailedAdd() {
      if (!this.savedSnapshot) return;

      this.saved = true;
      this.restoreSnapshotToFields();
      this.refreshSummaryFromFields();
      this.syncNoteProperty();
      this.setAddToCartState(true);
      this.updateSavedUI();
    }

    verifyPersonalizationInCartFromItems(items = [], linkId, mainVariantId) {
      // Nei bundle nativi la variante in carrello è il componente, non il parent:
      // non confrontare solo mainVariantId. Chiave affidabile: _personalization_id.
      if (!linkId || !items.length) return false;

      const hasPersonalizedMain = items.some((item) => {
        if (this.isEmbroideryFeeItem(item)) return false;
        const props = item.properties || {};
        if (props._personalization_id !== linkId) return false;
        return (
          hasEmbroideryText(props) ||
          Boolean(props[MELIDE_SNAPSHOT_PROPERTY]) ||
          Boolean(getEmbroideryProp(props, 'text', this.propertyLabels))
        );
      });

      if (this.feeIncluded) return hasPersonalizedMain;

      const hasFeeLine = items.some((item) => {
        const props = item.properties || {};
        return props._personalization_id === linkId && this.isEmbroideryFeeItem(item);
      });
      if (!hasFeeLine) return false;

      // Bundle: le props del parent possono non restare sulla riga componente;
      // fee + snapshot (_melide_snapshot) bastano a certificare l'add ricamo.
      const hasFeeSnapshot = items.some((item) => {
        const props = item.properties || {};
        return (
          props._personalization_id === linkId &&
          this.isEmbroideryFeeItem(item) &&
          Boolean(props[MELIDE_SNAPSHOT_PROPERTY])
        );
      });

      return hasPersonalizedMain || hasFeeSnapshot;
    }

    async verifyPersonalizationInCart(linkId, mainVariantId, addedItems = []) {
      if (this.verifyPersonalizationInCartFromItems(addedItems, linkId, mainVariantId)) {
        return true;
      }

      if (!linkId) return false;

      const response = await fetch(`${window.routes.cart_url}.js`, {
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) return false;

      const cart = await response.json();
      return this.verifyPersonalizationInCartFromItems(cart.items || [], linkId, mainVariantId);
    }

    async rollbackFailedPersonalizationAdd(linkId, mainVariantId) {
      // Se /cart/add ha creato righe ma la verify fallisce, non lasciare
      // magliette "nude" o fee orfane nel carrello.
      try {
        if (linkId) {
          await this.removePersonalizationFromCart(linkId);
        }
      } catch (error) {
        console.error(error);
      }

      if (!mainVariantId) return;

      try {
        const cart = await this.fetchCart();
        const updates = {};
        cart.items.forEach((item) => {
          const variantId = item.variant_id || item.id;
          const props = item.properties || {};
          if (variantId !== mainVariantId) return;
          if (props._personalization_id || hasEmbroideryText(props)) return;
          updates[item.key] = 0;
        });
        if (!Object.keys(updates).length) return;

        await fetch(window.routes.cart_update_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ updates }),
        });
      } catch (error) {
        console.error(error);
      }
    }

    getFormSnapshot() {
      return JSON.stringify({
        message: this.getMessageValue(),
        style: this.control('[data-customization-style]:checked')?.value || '',
        position: this.control('[data-customization-position]:checked')?.value || '',
        color: this.control('[data-customization-color]:checked')?.value || '',
        note: this.noteInput?.value || '',
        productColor: this.control('[data-customization-product-color]:checked')?.value || '',
      });
    }

    hasUnsavedChanges() {
      if (!this.drawer?.classList.contains('is-open')) return false;

      if (!this.savedSnapshot) {
        return this.getMessageValue().length > 0;
      }

      return this.getFormSnapshot() !== this.savedSnapshot;
    }

    formatMoney(amount) {
      const decimalAmount = Number(amount || 0);
      const moneyFormat = window.theme?.settings?.money_format;

      if (typeof formatPrice === 'function' && moneyFormat) {
        return formatPrice(decimalAmount * 100, moneyFormat);
      }

      return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
      }).format(decimalAmount);
    }

    getEmbroideryFeeAmount() {
      if (this.feeIncluded) return 0;
      return this.getCharCount() * this.charPrice;
    }

    getTotalPriceAmount() {
      if (this.feeIncluded) return this.basePriceCents / 100;
      return this.basePriceCents / 100 + this.getEmbroideryFeeAmount();
    }

    updatePricing() {
      const count = this.getCharCount();

      if (this.charCountEl) {
        this.charCountEl.textContent = `${count} lettere`;
      }
      if (this.inlineChars) {
        this.inlineChars.textContent = `${count} lettere`;
      }
      if (this.hiddenCharCount) {
        this.hiddenCharCount.value = String(count);
      }

      if (this.feeIncluded) {
        if (this.saved) {
          this.updateTotalPriceDisplay();
        }
        return;
      }

      const fee = this.getEmbroideryFeeAmount();

      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(fee);
      }
      if (this.inlineFee) {
        this.inlineFee.textContent = `+${this.formatMoney(fee)}`;
      }
      if (this.hiddenPrice) {
        this.hiddenPrice.value = this.formatMoney(fee);
      }

      if (this.saved) {
        this.updateTotalPriceDisplay();
      }
    }

    updateTotalPriceDisplay() {
      if (this.summaryTotalValue) {
        this.summaryTotalValue.textContent = this.formatMoney(this.getTotalPriceAmount());
      }
    }

    showValidationError(message) {
      if (this.validationError) {
        this.validationError.textContent = message;
        this.validationError.hidden = false;
      }
      if (this.saveError) {
        this.saveError.textContent = message;
        this.saveError.hidden = false;
      }
      this.saveError?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    hideValidationError() {
      if (this.validationError) this.validationError.hidden = true;
      if (this.saveError) {
        this.saveError.hidden = true;
        this.saveError.textContent = '';
      }
    }

    validateMessageInput(isSave = false) {
      if (!this.messageInput) return true;

      const cleaned = limitByCountableChars(
        sanitizeWhileTyping(this.messageInput.value, this.onlyNumbers),
        this.maxChars
      );
      if (cleaned !== this.messageInput.value) {
        this.messageInput.value = cleaned;
      }

      const charCount = this.getCharCount();

      if (isSave) {
        if (charCount === 0) {
          this.showValidationError('Inserisci il testo da ricamare.');
          this.messageInput.focus();
          return false;
        }
        if (charCount < this.minChars) {
          this.showValidationError(
            `Inserisci almeno ${this.minChars} caratteri (spazi esclusi).`
          );
          this.messageInput.focus();
          return false;
        }
      }

      return true;
    }

    onMessageInput() {
      const raw = this.messageInput.value;
      const disallowedPattern = this.onlyNumbers
        ? HAS_DISALLOWED_NUMBERS_PATTERN
        : HAS_DISALLOWED_PATTERN;
      const hadDisallowed = disallowedPattern.test(raw);

      let cleaned = limitByCountableChars(
        sanitizeWhileTyping(raw, this.onlyNumbers),
        this.maxChars
      );
      if (cleaned !== raw) {
        this.messageInput.value = cleaned;
      }

      if (hadDisallowed) {
        this.showValidationError(
          this.onlyNumbers
            ? 'Sono consentiti solo numeri.'
            : 'Alcuni caratteri non sono consentiti (es. emoji). Usa solo lettere, numeri, spazi, / e . , \' - &.'
        );
      } else {
        this.hideValidationError();
      }

      if (this.previewText) {
        this.updatePreviewTextContent();
      }

      this.autoGrowMessage();
      this.markDirty();
      this.updatePricing();
    }

    markDirty() {
      this.saved = false;
      this.setAddToCartState(false);
      this.updateSavedUI();
    }

    updatePreviewState(markDirty = true) {
      const position = this.control('[data-customization-position]:checked')?.value || 'Laterale';
      const style = this.control('[data-customization-style]:checked')?.value || 'Stampatello';
      const colorInput = this.control('[data-customization-color]:checked');
      const color = colorInput?.dataset.colorHex || '#000000';
      const colorName = colorInput?.dataset.label || colorInput?.value || '';
      const productColorInput = this.control('[data-customization-product-color]:checked');
      const productColorName = productColorInput?.dataset.label || productColorInput?.value || '';

      if (this.colorLabel) {
        this.colorLabel.textContent = colorName ? `— ${colorName}` : '';
      }
      if (this.productColorLabel) {
        this.productColorLabel.textContent = productColorName ? `— ${productColorName}` : '';
      }

      if (this.preview) {
        this.preview.className = 'product-customization__preview';
        let positionClass = position.toLowerCase();
        if (positionClass === 'sinistra' || positionClass === 'destra') {
          positionClass = 'laterale';
        }
        this.preview.classList.add(`product-customization__preview--position-${positionClass}`);
        this.preview.classList.add(`product-customization__preview--style-${style.toLowerCase()}`);
        const garment = this.garmentType || this.dataset.garmentType;
        if (garment === 'borsone') {
          this.preview.classList.add('product-customization__preview--borsone');
        }
      }

      if (this.previewText) {
        this.previewText.style.color = color;
        this.updatePreviewTextContent();
      }

      this.previewImages?.forEach((image) => {
        const side = image.dataset.customizationPreviewImage;
        const isActive =
          (position === 'Retro' && side === 'back') ||
          (position !== 'Retro' && side === 'front');
        image.classList.toggle('is-active', isActive);
      });

      if (markDirty) {
        this.markDirty();
      }
    }

    syncNoteProperty() {
      // no-op: le note finiscono in properties solo in addToCart().
    }

    updateSavedUI() {
      const isSaved = this.saved;

      this.savedNotice?.toggleAttribute('hidden', !isSaved);
      this.summaryTotal?.toggleAttribute('hidden', !isSaved);
      this.editBtn?.toggleAttribute('hidden', !isSaved);
      this.shareBtn?.toggleAttribute('hidden', !isSaved);
      this.openBtn?.classList.toggle('hidden', isSaved);

      if (isSaved) {
        this.updateTotalPriceDisplay();
      }
    }

    showShareFeedback(message) {
      if (!this.shareFeedback) return;

      this.shareFeedback.textContent = message;
      this.shareFeedback.hidden = false;

      clearTimeout(this.shareFeedbackTimeout);
      this.shareFeedbackTimeout = window.setTimeout(() => {
        this.shareFeedback.hidden = true;
      }, 2500);
    }

    async shareCustomization() {
      const message = this.getMessageValue();
      const style = this.summaryStyle?.textContent || '';
      const position = this.summaryPosition?.textContent || '';
      const color = this.summaryColor?.textContent || '';
      const productTitle = this.dataset.productTitle || document.title;
      const productUrl = `${window.location.origin}${this.dataset.productUrl || window.location.pathname}`;
      const shareText = `Personalizzazione ${productTitle}: "${message}" — ${style}, ${position}, ${color}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: productTitle,
            text: shareText,
            url: productUrl,
          });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }

      try {
        await navigator.clipboard.writeText(`${shareText}\n${productUrl}`);
        this.showShareFeedback('Copiato negli appunti');
      } catch (error) {
        console.error(error);
        this.showShareFeedback('Impossibile condividere');
      }
    }

    clearEditUrlParam() {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('edit_personalization')) {
          url.searchParams.delete('edit_personalization');
          window.history.replaceState({}, document.title, url.toString());
        }
      } catch (error) {
        /* no-op */
      }
    }

    resetForm(options = {}) {
      const { keepDrawerClosed = false } = options;

      this.editMode = false;
      this.cartPersonalizationId = null;
      this.cartEditLineKey = null;
      this.cartEditFeeLineKey = null;
      this.cartEditVariantId = null;
      this.cartEditQuantity = 1;
      this._restoringFromCart = false;
      this.saved = false;
      this.savedSnapshot = null;
      this.clearEditUrlParam();

      if (this.messageInput) this.messageInput.value = '';
      this.autoGrowMessage();
      if (this.noteInput) this.noteInput.value = '';
      this.hideValidationError();

      const firstPosition = this.control('[data-customization-position]');
      if (firstPosition) firstPosition.checked = true;
      const firstStyle = this.control('[data-customization-style]');
      if (firstStyle) firstStyle.checked = true;
      const firstColor = this.control('[data-customization-color]:not(:disabled)');
      if (firstColor) firstColor.checked = true;

      if (this.hiddenLinkId) this.hiddenLinkId.value = '';
      if (this.summaryText) this.summaryText.textContent = '—';
      if (this.summaryStyle) this.summaryStyle.textContent = '—';
      if (this.summaryPosition) this.summaryPosition.textContent = '—';
      if (this.summaryColor) this.summaryColor.textContent = '—';
      if (this.summaryNote) this.summaryNote.textContent = '—';
      if (!this.feeIncluded && this.summaryPrice) {
        this.summaryPrice.textContent = this.formatMoney(0);
      }

      this.summaryEl?.classList.add('is-empty');
      this.setSavedNotice(this.defaultSavedNoticeText);
      this.syncNoteProperty();
      this.updatePreviewState(false);
      this.updatePricing();
      this.setAddToCartState(false);
      this.updateSavedUI();

      if (!keepDrawerClosed && this.drawer?.classList.contains('is-open')) {
        this.closeDrawer();
      }
    }

    setProcessingState(active, options = {}) {
      const { message = 'Caricamento…', context = 'cart' } = options;
      this.isProcessing = active;

      if (active) {
        this.setAttribute('data-processing', context);
      } else {
        this.removeAttribute('data-processing');
      }
      this.productForm?.toggleAttribute('data-customization-processing', active);

      if (this.summarySpinnerMessageEl && message && (context === 'cart' || !active)) {
        this.summarySpinnerMessageEl.textContent = message;
      }
      if (this.drawerSpinnerMessageEl && message && (context === 'save' || !active)) {
        this.drawerSpinnerMessageEl.textContent = message;
      }

      this.summarySpinnerEl?.setAttribute('aria-hidden', active && context === 'cart' ? 'false' : 'true');
      this.drawerSpinnerEl?.setAttribute('aria-hidden', active && context === 'save' ? 'false' : 'true');

      if (active) {
        this.savedNotice?.setAttribute('hidden', '');
      } else if (this.saved) {
        this.savedNotice?.removeAttribute('hidden');
      }

      this.saveBtn?.toggleAttribute('aria-disabled', active);
      this.saveSpinner?.classList.toggle('hidden', !(active && context === 'save'));
      this.saveSpinner?.setAttribute('aria-hidden', active && context === 'save' ? 'false' : 'true');

      this.openBtn?.toggleAttribute('aria-disabled', active);
      this.editBtn?.toggleAttribute('aria-disabled', active);
      this.shareBtn?.toggleAttribute('aria-disabled', active);
      this.closeBtn?.toggleAttribute('aria-disabled', active);
      this.overlay?.classList.toggle('is-disabled', active);

      const submitButton = this.productForm?.querySelector('.product-form__submit');
      const submitSpinner = this.productForm?.querySelector('.loading__spinner');

      if (!submitButton) return;

      if (active) {
        submitButton.setAttribute('aria-disabled', 'true');
        if (context === 'cart') {
          submitButton.classList.add('loading');
          submitSpinner?.classList.remove('hidden');
        }
        return;
      }

      submitButton.classList.remove('loading');
      submitSpinner?.classList.add('hidden');
      this.setAddToCartState(this.saved);
    }

    async waitForPendingVariant(timeoutMs = 8000) {
      const started = Date.now();
      while (Date.now() - started < timeoutMs) {
        const pendingQueue = Boolean(this._pendingOptionQueue?.length);
        const pendingColor = Boolean(this._pendingDrawerColor);
        const pendingId = this._pendingVariantId;
        const liveId = this.getSelectedVariantId();
        const idMatches = Boolean(
          pendingId && liveId && String(liveId) === String(pendingId)
        );

        if (idMatches) {
          this._pendingDrawerColor = null;
          this._pendingVariantId = null;
          if (!pendingQueue) return true;
        } else if (!pendingQueue && !pendingColor && !pendingId) {
          return true;
        }

        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      return !(
        this._pendingOptionQueue?.length ||
        this._pendingDrawerColor ||
        this._pendingVariantId
      );
    }

    recoverUnavailableVariant() {
      const colorName =
        this.control('[data-customization-product-color]:checked')?.value ||
        this.getPdpColorValue();
      if (!colorName) return false;

      const best = this.findBestAvailableVariantForColor(colorName);
      if (!best?.id) return false;
      if (String(best.id) === String(this.getSelectedVariantId())) return false;

      this._pendingDrawerColor = colorName;
      this._pendingVariantId = best.id;
      this._pendingOptionQueue = [];
      if (this.setPdpOptionsTogether(best.options, best.id)) return true;

      this._pendingOptionQueue = this.diffVariantOptions(best);
      const first = this._pendingOptionQueue.shift();
      if (first && !this.setPdpOptionByIndex(first.index, first.value)) {
        this._pendingOptionQueue = [];
        this._pendingDrawerColor = null;
        this._pendingVariantId = null;
        return false;
      }
      return true;
    }

    postCartJson(url, payload) {
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        cache: 'no-store',
        body: JSON.stringify(payload),
      });
    }

    async saveCustomization() {
      if (this.isProcessing || melideCustomizationSaveLock || !this.validateMessageInput(true)) {
        return;
      }

      const message = this.getMessageValue();
      const charCount = this.getCharCount();

      const style = this.control('[data-customization-style]:checked')?.dataset.label || '';
      const position = this.control('[data-customization-position]:checked')?.dataset.label || '';
      const color = this.control('[data-customization-color]:checked')?.dataset.label || '';
      const note = (this.noteInput?.value || '').trim();
      const price = this.formatMoney(charCount * this.charPrice);
      // In edit mode riusa lo stesso id: altrimenti il link "Modifica" e le
      // righe fee si disallineano e le modifiche successive pescano dati vecchi.
      const linkId =
        this.editMode && this.cartPersonalizationId
          ? this.cartPersonalizationId
          : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      if (this.summaryText) this.summaryText.textContent = message;
      if (this.summaryStyle) this.summaryStyle.textContent = style;
      if (this.summaryPosition) this.summaryPosition.textContent = position;
      if (this.summaryColor) this.summaryColor.textContent = color;
      if (this.summaryNote) this.summaryNote.textContent = note || '—';
      if (!this.feeIncluded && this.summaryPrice) {
        this.summaryPrice.textContent = price;
      }
      if (this.hiddenLinkId) this.hiddenLinkId.value = linkId;

      this.syncNoteProperty();
      this.updatePricing();

      this.summaryEl?.classList.remove('is-empty');
      this.saved = true;
      this.savedSnapshot = this.getFormSnapshot();
      this.clearCartError();
      this.updateSavedUI();

      // Edit mode (arrivo dal carrello): aggiorna subito la riga carrello.
      // Il drawer resta aperto con loading finché l'operazione non è completata.
      if (this.editMode && this.cartPersonalizationId) {
        melideCustomizationSaveLock = true;
        try {
          this.hideValidationError();
          this.setProcessingState(true, {
            message: 'Aggiornamento personalizzazione nel carrello…',
            context: 'save',
          });
          const settled = await this.waitForPendingVariant();
          if (!settled) {
            this.showValidationError(
              'Attendi che colore e taglia si aggiornino, poi riprova a salvare.'
            );
            return;
          }

          let variantCheck = this.validateVariantForAdd();
          if (!variantCheck.ok && this.recoverUnavailableVariant()) {
            const recovered = await this.waitForPendingVariant();
            variantCheck = recovered
              ? this.validateVariantForAdd()
              : { ok: false, message: this.getVariantUnavailableMessage() };
          }
          if (!variantCheck.ok) {
            this.showValidationError(variantCheck.message);
            this.preserveSavedStateAfterFailedAdd();
            return;
          }

          const updated = await this.addItemsToCart(charCount, {
            processingContext: 'save',
            alreadyProcessing: true,
          });
          if (updated === false) return;
          this.editMode = false;
          this.cartPersonalizationId = null;
          this.cartEditLineKey = null;
          this.cartEditFeeLineKey = null;
          this.cartEditVariantId = null;
          this.closeDrawer();
        } catch (error) {
          console.error(error);
          this.showValidationError(
            error?.message || 'Errore durante il salvataggio della personalizzazione.'
          );
          this.preserveSavedStateAfterFailedAdd();
        } finally {
          melideCustomizationSaveLock = false;
          if (this.isProcessing) this.setProcessingState(false);
        }
        return;
      }

      this.setAddToCartState(true);
      this.setSavedNotice(this.defaultSavedNoticeText);
      this.closeDrawer();
    }

    setAddToCartState(enabled) {
      if (this.isProcessing) return;
      if (!this.productForm) return;

      if (enabled) {
        this.productForm.setAttribute('data-customization-saved', 'true');
      } else {
        this.productForm.removeAttribute('data-customization-saved');
      }

      // Il blocco del pulsante "Aggiungi al carrello" vale solo per i prodotti
      // con personalizzazione obbligatoria. Sui prodotti facoltativi l'utente
      // deve poter aggiungere anche la versione non personalizzata.
      // Include anche sticky ATC (.product-bar__submit).
      if (enabled) {
        const variantCheck = this.validateVariantForAdd();
        if (!variantCheck.ok) {
          this.productForm.toggleSubmitButton?.(
            true,
            /esaurit|sold out/i.test(variantCheck.message || '')
              ? window.variantStrings?.soldOut
              : window.variantStrings?.unavailable
          );
          if (this.required) this.syncSubmitButtons(true);
          this.syncCheckoutGate();
          return;
        }
        this.productForm.toggleSubmitButton?.(false);
      }

      if (this.required) {
        this.syncSubmitButtons(enabled);
      }

      this.syncCheckoutGate();
    }

    onFormSubmit(event) {
      if (this.isProcessing) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const charCount = this.getCharCount();

      // Personalizzazione salvata con testo → aggiunta al carrello (con o senza
      // riga supplemento ricamo a seconda di form_fee_product).
      if (this.saved && charCount > 0) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const variantCheck = this.validateVariantForAdd();
        if (!variantCheck.ok) {
          this.showCartError(variantCheck.message);
          this.preserveSavedStateAfterFailedAdd();
          return;
        }

        this.addItemsToCart(charCount);
        return;
      }

      // Prodotto che richiede la personalizzazione ma non ancora salvata → apri drawer.
      if (this.required) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openDrawer();
        return;
      }

      // Prodotto con personalizzazione facoltativa e nessun testo salvato →
      // aggiunta normale gestita dal product-form standard (maglietta liscia).
    }

    getCartSectionIds() {
      const sectionIds = new Set();
      this.getCartUiTargets().forEach((target) => {
        target.getSectionsToRender?.()?.forEach((section) => {
          if (section.section) sectionIds.add(section.section);
        });
      });
      return [...sectionIds];
    }

    getCartSectionPayload() {
      const sectionIds = this.getCartSectionIds();
      if (!sectionIds.length) return {};
      return {
        sections: sectionIds,
        sections_url: window.location.pathname,
      };
    }

    hasUsableCartSections(sections) {
      if (!sections || typeof sections !== 'object') return false;
      const ids = this.getCartSectionIds();
      if (!ids.length) return Boolean(sections['cart-drawer']);
      return ids.some((id) => typeof sections[id] === 'string' && sections[id].length > 0);
    }

    async fetchCartSectionsViaUpdate(sectionIds = this.getCartSectionIds()) {
      if (!sectionIds.length) return null;

      const cart = await this.fetchCart();
      const first = (cart.items || []).find((item) => item?.key);
      if (!first) return null;

      // No-op sulla quantità della prima riga: POST non in cache, HTML del
      // carrello attuale. Evita `updates: {}` che può restituire uno snapshot vecchio.
      const response = await this.postCartJson(window.routes.cart_change_url, {
        id: first.key,
        quantity: first.quantity,
        sections: sectionIds,
        sections_url: window.location.pathname,
      });

      if (!response.ok) return null;

      let data = {};
      try {
        data = await response.json();
      } catch (error) {
        return null;
      }

      if (data.status || data.errors) return null;
      return data.sections || null;
    }

    async resolveCartSections(preferredSections = null) {
      if (this.hasUsableCartSections(preferredSections)) {
        return preferredSections;
      }
      return this.fetchCartSectionsViaUpdate();
    }

    async fetchCartSections(cart) {
      if (!cart?.getSectionsToRender) return this.fetchAllCartSections();

      const sectionIds = cart
        .getSectionsToRender()
        .map((section) => section.section)
        .filter(Boolean);

      if (!sectionIds.length) return this.fetchAllCartSections();
      return this.fetchCartSectionsViaUpdate(sectionIds);
    }

    getCartUiTargets() {
      const targets = [];
      const seen = new Set();
      const cartDrawer = document.querySelector('cart-drawer');

      // cart-drawer-items è dentro cart-drawer e condivide sectionsToRender.
      // Renderizzarlo prima può lanciare su HTML inatteso e saltare il drawer.
      const selectors = cartDrawer
        ? ['cart-drawer', 'cart-items', 'cart-notification']
        : ['cart-drawer-items', 'cart-items', 'cart-notification'];

      selectors.forEach((selector) => {
        const element = document.querySelector(selector);
        if (!element || seen.has(element)) return;
        seen.add(element);
        targets.push(element);
      });

      return targets;
    }

    async fetchSectionsByIds(sectionIds) {
      return this.fetchCartSectionsViaUpdate(sectionIds);
    }

    async fetchAllCartSections() {
      return this.fetchCartSectionsViaUpdate();
    }

    extractCartSectionHTML(html, selector) {
      if (!html) return '';
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node =
        (selector && doc.querySelector(selector)) ||
        doc.querySelector('#CartDrawer-Body') ||
        doc.querySelector('.shopify-section');
      return node?.innerHTML || '';
    }

    cartDrawerShowsPersonalization(linkId, expectedText = '') {
      if (!linkId) return false;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return false;

      const hasLink = Array.from(
        drawer.querySelectorAll('a.cart-item__edit-personalization')
      ).some((link) => (link.getAttribute('href') || '').includes(linkId));
      if (!hasLink) return false;
      if (!expectedText) return true;

      const drawerText = drawer.textContent || '';
      return drawerText.includes(expectedText);
    }

    renderCartUiSections(sections, { openDrawer = false } = {}) {
      if (!sections) return;

      const cartDrawer = document.querySelector('cart-drawer');
      let drawerRendered = false;

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        try {
          cartDrawer.renderContents({ sections }, openDrawer);
          drawerRendered = true;
        } catch (error) {
          console.error(error);
        }
      }

      if (!drawerRendered && sections['cart-drawer']) {
        const cartBody = document.querySelector('#CartDrawer-Body');
        const inner = this.extractCartSectionHTML(
          sections['cart-drawer'],
          '#shopify-section-cart-drawer #CartDrawer-Body'
        );
        if (cartBody && inner) {
          cartBody.innerHTML = inner;
        }
        if (openDrawer) cartDrawer?.open?.();
      }

      this.getCartUiTargets().forEach((target) => {
        if (target.tagName === 'CART-DRAWER') return;

        try {
          if (typeof target.renderContents === 'function') {
            target.renderContents({ sections }, false);
            return;
          }

          target.getSectionsToRender?.()?.forEach((section) => {
            const elementToReplace =
              document.querySelector(section.selector) || document.querySelector(section.id);

            if (!elementToReplace || !sections[section.section]) return;

            const inner = this.extractCartSectionHTML(sections[section.section], section.selector);
            if (!inner) return;
            elementToReplace.innerHTML = inner;
          });
        } catch (error) {
          console.error(error);
        }
      });

      if (typeof updateCartCounters === 'function') {
        updateCartCounters();
      }
      if (typeof handleCartInputNames === 'function') {
        handleCartInputNames();
      }
      if (typeof updateFreeShipping === 'function') {
        updateFreeShipping();
      }
    }

    async refreshCartUi(options = {}) {
      const { openDrawer = false, sections = null } = options;
      const resolvedSections = sections || (await this.fetchAllCartSections());

      if (typeof publish === 'function') {
        publish(typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.cartUpdate : 'cart-update', {
          source: 'product-customization',
        });
      }

      this.renderCartUiSections(resolvedSections, { openDrawer });

      if (openDrawer) {
        document.querySelector('cart-drawer')?.open?.();
      }
    }

    async refreshCartDrawer(cart, sections, { openDrawer = true } = {}) {
      await this.refreshCartUi({ sections, openDrawer });
    }

    async updateCartLinesInPlace({
      linkId,
      mainVariantId,
      quantity,
      charCount,
      mainProperties,
      feeProperties,
      feeVariantId,
    }) {
      const cart = await this.fetchCart();
      const main =
        (this.cartEditLineKey && cart.items.find((item) => item.key === this.cartEditLineKey)) ||
        this.findCartItemByPersonalizationId(cart.items, linkId);
      if (!main) return null;

      const currentVariantId = main.variant_id || main.id;
      if (String(currentVariantId) !== String(mainVariantId)) return null;

      const sectionPayload = this.getCartSectionPayload();
      const mainResponse = await this.postCartJson(window.routes.cart_change_url, {
        id: main.key,
        quantity,
        properties: mainProperties,
        ...sectionPayload,
      });
      let data = {};
      try {
        data = await mainResponse.json();
      } catch (error) {
        data = {};
      }
      if (!mainResponse.ok || data.status) {
        throw new Error(this.getCartAddErrorMessage(mainResponse, data));
      }

      if (this.feeIncluded || !feeVariantId) return data;

      const liveItems = data.items || [];
      const fee = liveItems.find(
        (item) =>
          this.isEmbroideryFeeItem(item) && item.properties?._personalization_id === linkId
      );
      const feeQty = charCount * quantity;

      if (fee) {
        const feeResponse = await this.postCartJson(window.routes.cart_change_url, {
          id: fee.key,
          quantity: feeQty,
          properties: feeProperties,
          ...sectionPayload,
        });
        try {
          data = await feeResponse.json();
        } catch (error) {
          data = {};
        }
        if (!feeResponse.ok || data.status) {
          throw new Error(this.getCartAddErrorMessage(feeResponse, data));
        }
        return data;
      }

      const addFee = await this.postCartJson(window.routes.cart_add_url, {
        items: [{ id: feeVariantId, quantity: feeQty, properties: feeProperties }],
        ...sectionPayload,
      });
      try {
        data = await addFee.json();
      } catch (error) {
        data = {};
      }
      if (!addFee.ok || data.status) {
        throw new Error(this.getCartAddErrorMessage(addFee, data));
      }
      return data;
    }

    async addItemsToCart(charCount, options = {}) {
      const {
        silent = false,
        skipReplace = false,
        processingContext = 'cart',
        alreadyProcessing = false,
      } = options;

      if (this.isProcessing && !alreadyProcessing) return false;

      await this.waitForPendingVariant();

      const formData = new FormData(this.form);
      const variantCheck = this.validateVariantForAdd();

      if (!variantCheck.ok) {
        if (!silent) {
          this.showCartError(variantCheck.message);
          this.preserveSavedStateAfterFailedAdd();
        }
        throw new Error(variantCheck.message);
      }

      const processingMessage =
        processingContext === 'save'
          ? 'Aggiornamento personalizzazione nel carrello…'
          : 'Aggiunta al carrello in corso…';

      if (!silent && !alreadyProcessing) {
        this.setProcessingState(true, {
          message: processingMessage,
          context: processingContext,
        });
      }

      const mainVariantId = variantCheck.variant.id;
      const feeVariantId = this.feeVariantId ? parseInt(this.feeVariantId, 10) : null;
      const quantity =
        this.editMode && this.cartEditQuantity
          ? this.cartEditQuantity
          : parseInt(formData.get('quantity') || '1', 10);
      const linkId = this.hiddenLinkId?.value || '';

      // Conserva eventuali proprietà aggiunte da altre app (mantengono name).
      // Le chiavi ricamo le impostiamo dopo, così non vengono sovrascritte
      // da input hidden stantii nel form.
      const extraProperties = {};
      const embroideryKeys = new Set([
        ...Object.values(this.propertyLabels || {}),
        ...Object.values(MELIDE_ADMIN_LABELS),
        ...Object.values(MELIDE_ADMIN_LABELS).map((key) => `_${key}`),
        MELIDE_LETTERS_KEY,
        MELIDE_SNAPSHOT_PROPERTY,
        '_personalization_id',
        '_is_embroidery_fee',
      ]);
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && !embroideryKeys.has(match[1])) extraProperties[match[1]] = value;
      });

      // Le proprietà del ricamo vengono lette direttamente dai controlli (non
      // hanno name="properties[...]") e normalizzate prima dell'invio.
      const note = (this.noteInput?.value || '').trim();
      const position = this.control('[data-customization-position]:checked')?.value || '';
      const style = this.control('[data-customization-style]:checked')?.value || '';
      const color = this.control('[data-customization-color]:checked')?.value || '';
      const message = encodeLineBreaks(this.getMessageValue());
      const price = this.formatMoney(charCount * this.charPrice);
      const labels = this.propertyLabels;

      // Snapshot / Order Printer: sempre label corte IT.
      const canonical = {
        [MELIDE_ADMIN_LABELS.text]: message,
        [MELIDE_ADMIN_LABELS.cost]: price,
      };
      if (position) canonical[MELIDE_ADMIN_LABELS.position] = position;
      if (style) canonical[MELIDE_ADMIN_LABELS.font] = style;
      if (color) canonical[MELIDE_ADMIN_LABELS.color] = color;
      if (note) canonical[MELIDE_ADMIN_LABELS.notes] = note;

      // Checkout: label corte localizzate. Backoffice IT: stesse (o con _ se locale ≠ IT).
      const properties = { ...extraProperties };
      const orderedFields = [
        { field: 'cost', value: this.feeIncluded ? '' : price },
        { field: 'text', value: message },
        { field: 'color', value: color },
        { field: 'position', value: position },
        { field: 'font', value: style },
        { field: 'notes', value: note },
      ];

      for (const { field, value } of orderedFields) {
        if (!value) continue;
        const localeKey = labels[field] || MELIDE_ADMIN_LABELS[field];
        const adminKey = MELIDE_ADMIN_LABELS[field];
        properties[localeKey] = value;
        if (localeKey !== adminKey) {
          properties[`_${adminKey}`] = value;
        }
      }

      // Unica property tecnica (nascosta): conteggio lettere.
      properties[MELIDE_LETTERS_KEY] = String(charCount);

      const productTitle = this.dataset.productTitle || '';
      const melideSnapshot = serializeMelideSnapshot(canonical, {
        productTitle,
        linkId,
      });

      const mainProperties = {
        ...properties,
        _personalization_id: linkId,
        ...(melideSnapshot ? { [MELIDE_SNAPSHOT_PROPERTY]: melideSnapshot } : {}),
      };
      const feeProperties = {
        _personalization_id: linkId,
        _is_embroidery_fee: '1',
        ...(melideSnapshot ? { [MELIDE_SNAPSHOT_PROPERTY]: melideSnapshot } : {}),
      };

      // Snapshot su prodotto E fee: sui bundle le props del parent possono
      // spostarsi sul group; la copia sulla fee resta leggibile in ordine/OP.
      const items = [
        {
          id: mainVariantId,
          quantity,
          properties: mainProperties,
        },
      ];

      if (!this.feeIncluded && feeVariantId) {
        items.push({
          id: feeVariantId,
          quantity: charCount * quantity,
          properties: feeProperties,
        });
      }

      try {
        let data = {};
        let usedInPlaceUpdate = false;

        // Stessa variante: aggiorna le properties sulla riga esistente.
        // Evita remove+add che dopo 2-3 edit lascia snapshot/id vecchi nel drawer.
        if (!skipReplace && this.editMode && this.cartPersonalizationId) {
          try {
            const inPlace = await this.updateCartLinesInPlace({
              linkId,
              mainVariantId,
              quantity,
              charCount,
              mainProperties,
              feeProperties,
              feeVariantId,
            });
            if (inPlace) {
              const cartNow = await this.fetchCart();
              const live = this.findCartItemByPersonalizationId(cartNow.items, linkId);
              const liveText = getEmbroideryProp(live?.properties || {}, 'text', this.propertyLabels);
              const liveColor = getEmbroideryProp(live?.properties || {}, 'color', this.propertyLabels);
              const livePosition = getEmbroideryProp(live?.properties || {}, 'position', this.propertyLabels);
              const liveFont = getEmbroideryProp(live?.properties || {}, 'font', this.propertyLabels);
              const propertiesMatch =
                live &&
                liveText === message &&
                (!color || liveColor === color) &&
                (!position || livePosition === position) &&
                (!style || liveFont === style);
              if (propertiesMatch) {
                data = inPlace;
                usedInPlaceUpdate = true;
              }
            }
          } catch (inPlaceError) {
            console.error(inPlaceError);
          }
        }

        if (!usedInPlaceUpdate) {
          const response = await this.postCartJson(window.routes.cart_add_url, {
            items,
            ...this.getCartSectionPayload(),
          });

          try {
            data = await response.json();
          } catch (parseError) {
            data = {};
          }

          if (!response.ok || data.status) {
            throw new Error(this.getCartAddErrorMessage(response, data));
          }

          if (!skipReplace && this.editMode && this.cartPersonalizationId) {
            const stale = await this.removeStalePersonalizationLines(
              linkId,
              mainVariantId,
              feeVariantId,
              charCount * quantity
            );
            if (stale?.items) data = stale;
          }
        }

        const addedToCart = await this.verifyPersonalizationInCart(
          linkId,
          mainVariantId,
          data.items || []
        );
        if (!addedToCart) {
          if (this.editMode && this.cartEditVariantId) {
            await this.removeStalePersonalizationLines(
              linkId,
              this.cartEditVariantId,
              feeVariantId
            );
          } else {
            await this.rollbackFailedPersonalizationAdd(linkId, mainVariantId);
          }
          throw new Error(
            'La personalizzazione non è stata aggiunta correttamente al carrello. Riprova: salva di nuovo il ricamo e poi aggiungi al carrello.'
          );
        }

        this.cartPersonalizationId = linkId;
        this.clearCartError();

        let sections = data.sections || null;
        if (!this.hasUsableCartSections(sections)) {
          const freshSections = await this.fetchCartSectionsViaUpdate();
          if (this.hasUsableCartSections(freshSections)) {
            sections = freshSections;
          }
        }

        const openDrawer = !silent;
        await this.refreshCartUi({ sections, openDrawer });

        const expectedText = this.getMessageValue();
        if (
          linkId &&
          document.querySelector('cart-drawer') &&
          !this.cartDrawerShowsPersonalization(linkId, expectedText)
        ) {
          const retrySections = await this.fetchCartSectionsViaUpdate();
          if (this.hasUsableCartSections(retrySections)) {
            await this.refreshCartUi({ sections: retrySections, openDrawer });
          }
        }

        if (!silent && !document.querySelector('cart-drawer')) {
          window.location = window.routes.cart_url;
          return true;
        }

        // Aggiunta verificata nel carrello: solo ora si ripulisce la PDP così
        // l'utente può aggiungere una nuova personalizzazione indipendente.
        if (!silent) {
          this.resetForm({ keepDrawerClosed: true });
        }
        return true;
      } catch (error) {
        console.error(error);
        if (this.editMode && this.cartPersonalizationId && this.cartEditVariantId) {
          try {
            await this.removeStalePersonalizationLines(
              this.cartPersonalizationId,
              this.cartEditVariantId,
              this.feeVariantId ? parseInt(this.feeVariantId, 10) : null
            );
          } catch (cleanupError) {
            console.error(cleanupError);
          }
        }
        if (!silent) {
          this.showCartError(error.message || 'Errore durante l\'aggiunta al carrello.');
          this.preserveSavedStateAfterFailedAdd();
        }
        throw error;
      } finally {
        if (!silent) {
          this.setProcessingState(false);
        }
      }
    }
  }

  customElements.define('product-customization', ProductCustomization);
})();
